// Quadro das páginas (0.47, refeito na 0.65 com o "Padrão das páginas").
// Folha infinita travada à esquerda e em cima, que cresce sozinha para a direita e para baixo.
// Ferramentas: Texto, Caneta, Borracha, ↶ voltar, ↷ avançar, ⛶ tela cheia e ⋯ Mais (Inserir, Ferramentas, Folha, Página).
// Texto 09 (G1–G5): botões grandes (44 pt), "Esconder" no canto oposto ao "Mais", tamanhos em menuzinho por cima,
// tela cheia com barra de uma linha arrastável e "Sair", texto assentado nas linhas da folha e a Régua.
// Gestos: a ferramenta escolhida continua até trocar; dois dedos dão zoom e andam pela folha; segurar num elemento
// seleciona; segurar num lugar vazio mostra o ✥ para arrastar a folha.
// 0.72 (textos 11b e 11c): barra em duas linhas com ícone em cima e nome embaixo (Texto · Caneta · Marca-texto · Borracha /
// Esconder · Régua · Voltar · Refazer · Tela cheia · Mais), fixa no topo ao rolar a página, opções de cada ferramenta com
// bolinhas + barrinha + prévia, tamanho da letra por trecho, régua que estica, tela cheia com barra que recolhe e anda,
// toque duplo seleciona a palavra, mover caixa sem rolar a tela e fundo da folha cobrindo toda a área visível.
// As medidas ficam numa escala em que 1000 = a largura da tela, então o quadro se ajusta a qualquer aparelho.
import { LARGURA_LEITURA, useTamanho } from '../lib/tamanho';
import { createContext, useContext, useEffect, useMemo, useRef, useState, type ComponentType, type ReactNode } from 'react';
import { Alert, Animated, Dimensions, Image, Keyboard, Modal, PanResponder, Platform, Pressable, ScrollView, Text, TextInput, View, type NativeScrollEvent, type NativeSyntheticEvent, type ScrollViewProps } from 'react-native';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { ScrollArrastavel, useAreaRolagem } from './Arrastar';
import { ROLAGEM_TECLADO, useTeclado } from './teclado';
import { useBloquearVoltarBorda } from './VoltarBorda';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import Svg, { Circle, G, Line, Path, Rect, Text as SvgText } from 'react-native-svg';
import * as Haptics from 'expo-haptics';
import type { AbaId, Folha, Item, Quadro as Q, QuadroCheck, QuadroTabela, QuadroTexto, Traco } from '../store/types';
import { Chip } from './ui';
import { C, alpha, modoDoApp } from './theme';
import { FolhaFundo } from './FolhaFundo';
import { PaletaCores } from './PaletaCores';
import { fonteImagem } from './Anexos';
import { useAnexar } from './MenuAnexo';
import { uid } from '../lib/date';
import { N_, t } from '../lib/i18n';
import { CORES_FOLHA, FOLHA_PADRAO, TEMAS_FOLHA, TIPOS_FOLHA, corNaFolha, folhaEscura, fundoFolha, salvarBolinha, salvarFerramenta, useConfigPaginas } from '../lib/paginas';
import { NIVEIS_TEXTO, TAM_MAX, TAM_MIN, TAM_NORMAL, aplicarEstilo, ajustarTrechos, estiloNoPonto, larguraLetra, medidasTexto, nivelDoTexto, numeroDoTam, palavraEm, segmentos, yAssentado, type EstiloTrecho } from '../lib/textoFolha';
import { ajeitarForma, caminhoForma, type Forma } from '../lib/formas';
import { escolherEGuardar } from '../lib/nuvem';
import { exportarQuadro } from '../lib/exportarQuadro';
import { Ditado } from './Ditado';
import { useAlvoGuia, usePreparoTela } from '../lib/guia/destaque';

export const LARG_BASE = 1000;
export const CORES = ['#F5F5F5', '#111827', '#EF4444', '#F59E0B', '#10B981', '#3B82F6', '#8B5CF6', '#EC4899'];
export const ESPESSURAS: [string, number][] = [[N_('Fina'), 3], [N_('Média'), 6], [N_('Grossa'), 12]];
export const MARCA = 'rgba(255,235,59,0.45)';   // marca-texto (cor antiga, padrão)
// 0.72 (texto 11b B3/B4): 4 bolinhas de espessura (mini, média, grande, bem grande) + barrinha, por ferramenta
const ESCALA_BARRA_MIN = 0.75;   // texto 22: menor tamanho da barra flutuante (botões ainda fáceis de tocar e nomes legíveis)
const DOTS_CANETA = [3, 6, 12, 20], DOTS_MARCA = [12, 20, 26, 40], DOTS_BORRACHA = [10, 22, 34, 45];
const FAIXA_CANETA: [number, number] = [1, 30], FAIXA_MARCA: [number, number] = [6, 60], FAIXA_BORRACHA: [number, number] = [6, 80];
const CORES_MARCA = ['#FDE047', '#86EFAC', '#F9A8D4', '#93C5FD'];   // tons claros do marca-texto
const TAMANHOS_A = NIVEIS_TEXTO.map(n => n.tam);                    // os 4 "A" do tamanho da letra
const TAM_CELULA = 34;
const rgba = (hex: string, a: number) => {
  const h = hex.replace('#', ''), n = parseInt(h.length === 3 ? h.split('').map(x => x + x).join('') : h.slice(0, 6), 16) || 0;
  return `rgba(${(n >> 16) & 255},${(n >> 8) & 255},${n & 255},${Math.round(a * 100) / 100})`;
};
const maisPerto = (vals: number[], v: number) => vals.reduce((m, x, i) => (Math.abs(x - v) < Math.abs(vals[m] - v) ? i : m), 0);
// Cada ferramenta lembra a última cor, espessura e transparência (fica guardado neste aparelho)
type Memoria = { bol: number; esp: number; bolM: number; espM: number; transp: number; raio: number; bolTxt: number; coresMarca?: string[] };
const CHAVE_MEMORIA = 'mp.quadro.ferramentas';
let memoria: Memoria = { bol: 0, esp: 6, bolM: 0, espM: 26, transp: 55, raio: 22, bolTxt: 0 };
AsyncStorage.getItem(CHAVE_MEMORIA).then(v => { if (v) memoria = { ...memoria, ...JSON.parse(v) }; }).catch(() => {});
const lembrar = (p: Partial<Memoria>) => { memoria = { ...memoria, ...p }; AsyncStorage.setItem(CHAVE_MEMORIA, JSON.stringify(memoria)).catch(() => {}); };
// texto 18 A6: "Esconder" vale enquanto o app está aberto (a próxima página abre com as ferramentas escondidas)
let barraEscondida = false;
const BORDA = 160, CRESCE = 600;
const MARGEM_NOVA = 200;   // texto 20 (3): uns 3 a 4 dedos antes da 1ª linha (múltiplo de 40: as linhas continuam na mesma grade)
const TAM_TITULO = 64;

export const quadroVazio = (): Q => ({ tracos: [], textos: [], checks: [], larg: LARG_BASE, alt: 1400, margem: MARGEM_NOVA });
/** Página nova (C11): já começa com o "Título" no topo da folha (pode ser apagado). */
export const quadroComTitulo = (): Q => ({ ...quadroVazio(), textos: [{ id: uid(), x: 40, y: 20, w: 900, texto: '', tam: TAM_TITULO, b: true, titulo: true }] });
export const temConteudo = (q?: Q | null) => !!q && (q.tracos.length > 0 || q.textos.some(x => x.texto.trim()) || q.checks.some(c => c.itens.some(i => i.text.trim()))
  || !!q.tabelas?.length || !!q.imagens?.length);
/** Texto do "Título" escrito no topo da folha. */
export const tituloDaFolha = (q?: Q | null) => q?.textos.find(x => x.titulo)?.texto.trim() ?? '';
/** Primeiro texto escrito no quadro (para dar nome à página sem título). */
export const primeiroTexto = (q?: Q | null) => (tituloDaFolha(q) || q?.textos.map(x => x.texto.trim()).find(Boolean) || q?.checks.flatMap(c => c.itens).map(i => i.text.trim()).find(Boolean) || '');

export type Modo = 'texto' | 'caneta' | 'borracha' | 'marca' | 'selecionar' | 'mover';
type P = [number, number];
const caminho = (pts: P[]) => pts.map(([x, y], i) => `${i ? 'L' : 'M'}${x} ${y}`).join('') + (pts.length === 1 ? `L${pts[0][0] + 0.1} ${pts[0][1]}` : '');

// ─── Borracha parcial e seleção (0.52) ───────────────────────────────────────────────────────
/** Lê os pontos de um traço salvo ("M x yL x y..."). Cada "M" começa um pedaço novo. */
export function pontosDe(d: string): P[][] {
  const partes: P[][] = [];
  const re = /([MLml])\s*(-?[\d.]+)[\s,]+(-?[\d.]+)/g;
  let m: RegExpExecArray | null;
  while ((m = re.exec(d))) {
    const p: P = [Number(m[2]), Number(m[3])];
    if (m[1] === 'M' || m[1] === 'm' || !partes.length) partes.push([p]); else partes[partes.length - 1].push(p);
  }
  return partes;
}
const r1 = (n: number) => Math.round(n * 10) / 10;
const caminhoPartes = (partes: P[][]) => partes.map(pts => caminho(pts.map(([x, y]) => [r1(x), r1(y)] as P))).join('');
const distSeg = (p: P, a: P, b: P) => {
  const dx = b[0] - a[0], dy = b[1] - a[1], l = dx * dx + dy * dy;
  const k = l ? Math.max(0, Math.min(1, ((p[0] - a[0]) * dx + (p[1] - a[1]) * dy) / l)) : 0;
  return Math.hypot(p[0] - a[0] - k * dx, p[1] - a[1] - k * dy);
};
/** Distância do ponto ao traço inteiro. */
export const distTraco = (p: P, partes: P[][]) => {
  let m = Infinity;
  for (const pts of partes) {
    if (pts.length === 1) m = Math.min(m, Math.hypot(p[0] - pts[0][0], p[1] - pts[0][1]));
    for (let i = 1; i < pts.length; i++) m = Math.min(m, distSeg(p, pts[i - 1], pts[i]));
  }
  return m;
};
/** Apaga só o pedaço dos traços que fica dentro do círculo da borracha; o resto do traço continua. */
export function apagarPedaco(tracos: Traco[], p: P, raio: number): Traco[] {
  const out: Traco[] = [];
  for (const tr of tracos) {
    const partes = pontosDe(tr.d);
    const lim = raio + tr.w / 2;
    if (distTraco(p, partes) > lim) { out.push(tr); continue; }
    const novas: P[][] = [];
    for (const pts of partes) {
      const densos: P[] = [];
      pts.forEach((q, i) => {
        if (i) {
          const a = pts[i - 1], dist = Math.hypot(q[0] - a[0], q[1] - a[1]), n = Math.ceil(dist / 3);
          for (let k = 1; k < n; k++) densos.push([a[0] + ((q[0] - a[0]) * k) / n, a[1] + ((q[1] - a[1]) * k) / n]);
        }
        densos.push(q);
      });
      let run: P[] = [];
      for (const q of densos) {
        if (Math.hypot(q[0] - p[0], q[1] - p[1]) <= lim) { if (run.length > 1) novas.push(run); run = []; }
        else run.push(q);
      }
      if (run.length > 1) novas.push(run);
    }
    novas.forEach(pts => out.push({ ...tr, d: caminhoPartes([pts]) }));
  }
  return out;
}
/** Move um traço inteiro. */
export const moverTraco = (tr: Traco, dx: number, dy: number): Traco =>
  ({ ...tr, d: caminhoPartes(pontosDe(tr.d).map(pts => pts.map(([x, y]) => [x + dx, y + dy] as P))) });
const caixaTraco = (tr: Traco) => {
  const pts = pontosDe(tr.d).flat();
  if (!pts.length) return { x: 0, y: 0, w: 0, h: 0 };
  const xs = pts.map(q => q[0]), ys = pts.map(q => q[1]), m = tr.w / 2 + 6;
  const x = Math.min(...xs) - m, y = Math.min(...ys) - m;
  return { x, y, w: Math.max(...xs) + m - x, h: Math.max(...ys) + m - y };
};

type Tipo = 'texto' | 'check' | 'tabela' | 'imagem';
type Sel = { tipo: 'traco'; i: number } | { tipo: Tipo; id: string };
type Edit = { tipo: 'texto' | 'check' | 'tabela'; id: string } | null;
const ESPERA_SEGURAR = 450, ESPERA_FORMA = 700;
const vibrar = () => { if (Platform.OS !== 'web') Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Light).catch(() => {}); };

// ─── Régua (G5) ───────────────────────────────────────────────────────────────────────────────
// Fica por cima da folha (não sai no PDF nem na imagem). Medidas na escala do quadro: centro (x, y), ângulo a em graus
// e comprimento c (0.72, texto 11b B6: puxar uma das pontas estica ou encolhe a régua).
type Regua = { on: boolean; x: number; y: number; a: number; c: number };
const REGUA_C = 800, REGUA_L = 110, PERTO_REGUA = 45, REGUA_MIN = 200, REGUA_MAX = 6000, PONTA = 70;
const ANGULOS_REGUA = [0, 30, 45, 60, 90, 120, 135, 150, 180];
const rad = (a: number) => (a * Math.PI) / 180;
/** Ponto da folha nas medidas da régua (x ao longo dela, y atravessando). */
const paraRegua = (g: Regua, p: P): P => { const c = Math.cos(rad(g.a)), sn = Math.sin(rad(g.a)), dx = p[0] - g.x, dy = p[1] - g.y; return [dx * c + dy * sn, -dx * sn + dy * c]; };
const daRegua = (g: Regua, l: P): P => { const c = Math.cos(rad(g.a)), sn = Math.sin(rad(g.a)); return [g.x + l[0] * c - l[1] * sn, g.y + l[0] * sn + l[1] * c]; };
/** Dedo em cima da régua (a faixa da borda fica livre para a caneta encostar). */
const naRegua = (g: Regua, p: P) => { const [lx, ly] = paraRegua(g, p); return Math.abs(lx) <= g.c / 2 && Math.abs(ly) <= REGUA_L / 2 - 8; };
/** Dedo numa das pontas da régua (−1 esquerda, 1 direita, 0 fora): puxar estica. */
const pontaRegua = (g: Regua, p: P) => { const [lx, ly] = paraRegua(g, p); if (Math.abs(ly) > REGUA_L / 2 + 10) return 0; const d = g.c / 2 - Math.abs(lx); return d >= -30 && d <= PONTA ? (lx < 0 ? -1 : 1) : 0; };
/** Lado da régua em que a caneta começou perto da borda: −1 (de cima), 1 (de baixo) ou 0 (longe). */
const ladoRegua = (g: Regua, p: P) => {
  const [lx, ly] = paraRegua(g, p);
  if (Math.abs(lx) > g.c / 2 + 30) return 0;
  const d = Math.abs(ly) - REGUA_L / 2;
  return d >= -8 && d <= PERTO_REGUA ? (ly < 0 ? -1 : 1) : 0;
};
/** Leva o ponto para a borda da régua (o traço "gruda" nela). */
const naBorda = (g: Regua, lado: number, w: number, p: P): P => { const [lx] = paraRegua(g, p); const q = daRegua(g, [lx, lado * (REGUA_L / 2 + w / 2)]); return [r1(q[0]), r1(q[1])]; };
/** Encaixe suave em 0°, 30°, 45°, 60°, 90°... (até 4° de distância). */
const encaixar = (a: number) => { const m = ((a % 180) + 180) % 180; for (const alvo of ANGULOS_REGUA) if (Math.abs(m - alvo) < 4) return a + alvo - m; return a; };
const grausRegua = (a: number) => Math.round(((-a % 180) + 180) % 180) % 180;
type Canto = 'te' | 'td' | 'be' | 'bd';

const listas = (q: Q) => ({ textos: q.textos, checks: q.checks, tabelas: q.tabelas ?? [], imagens: q.imagens ?? [] });
const chaveLista: Record<Tipo, 'textos' | 'checks' | 'tabelas' | 'imagens'> = { texto: 'textos', check: 'checks', tabela: 'tabelas', imagem: 'imagens' };

export type QuadroProps = {
  valor: Q; onChange: (q: Q) => void; cor: string;
  aba?: AbaId | null;                 // de qual aba é a página (padrão das páginas da aba)
  altura?: number;                    // altura fixa; sem = ocupa o espaço
  /** texto 20 (7): a folha vai até o pé da tela (altura da página menos o que vem antes dela e a barra);
   *  "Esconder" a barra faz a folha crescer. Nunca fica menor que a altura dada. */
  encherTela?: boolean;
  telaCheia?: boolean;                // tela cheia de verdade: barra flutuante e compacta
  onTelaCheia?: () => void;           // botão ⛶ (entrar ou sair)
  onAplicarTodas?: (f: Folha) => void; rotuloTodas?: string;   // "Aplicar a todas as páginas desta matéria"
  simples?: boolean;                  // assinatura: só caneta e borracha, folha lisa
  nome?: string;                      // nome do arquivo ao exportar
  modoInicial?: Modo;
  onRolagem?: (pode: boolean) => void;
  extra?: ReactNode;
  /** @deprecated (0.47) — mantido para telas antigas */
  ferramentas?: string[]; onModo?: (m: string) => void;
};

export function Quadro(props: QuadroProps) {
  const { valor, onChange, cor, aba, altura, telaCheia, onTelaCheia, onAplicarTodas, rotuloTodas, simples, nome, onRolagem } = props;
  const { config, ferramenta, cantoBarra } = useConfigPaginas(aba);
  const ins = useSafeAreaInsets();
  const escuroApp = modoDoApp() === 'escuro';
  const folhaBase: Folha = simples ? { ...FOLHA_PADRAO, tipo: 'lisa' } : valor.folha ?? config.folha;
  // texto 20 (3): páginas novas (e as vazias) têm um espaço antes da 1ª linha; as já escritas não mudam de lugar
  const margemEf = simples ? undefined : valor.margem ?? (temConteudo(valor) ? undefined : MARGEM_NOVA);
  const folha: Folha = margemEf != null ? { ...folhaBase, topo: margemEf } : folhaBase;
  const fundo = fundoFolha(folha, escuroApp);
  const escura = folhaEscura(folha, escuroApp);
  const tinta = (c: string) => corNaFolha(c, escura);

  const inicial = (props.modoInicial ?? (simples ? 'caneta' : (ferramenta as Modo) || 'texto')) as Modo;
  const [modo, setModoSo] = useState<Modo>(['texto', 'caneta', 'borracha', 'marca', 'selecionar', 'mover'].includes(inicial) ? inicial : 'texto');
  const setModo = (m: Modo) => { setModoSo(m); if (!simples) salvarFerramenta(m); setSel(null); setEdit(null); };
  // 0.70 (texto 09, E3): com caneta, borracha ou marca-texto escolhidos, o gesto de voltar da borda esquerda fica desligado
  useBloquearVoltarBorda(modo === 'caneta' || modo === 'borracha' || modo === 'marca');
  // 0.72 (texto 11b B3): cada ferramenta lembra a última cor, espessura e transparência
  const [bol, setBolS] = useState(memoria.bol);           // bolinha da caneta
  const [bolTxt, setBolTxtS] = useState(memoria.bolTxt);  // bolinha do texto
  const [esp, setEspS] = useState(memoria.esp);
  const [bolM, setBolMS] = useState(memoria.bolM);        // marca-texto: cor, espessura e transparência (%)
  const [espM, setEspMS] = useState(memoria.espM);
  const [transp, setTranspS] = useState(memoria.transp);
  const [raio, setRaioS] = useState(memoria.raio);
  const setBol = (v: number) => { setBolS(v); lembrar({ bol: v }); };
  const setBolTxt = (v: number) => { setBolTxtS(v); lembrar({ bolTxt: v }); };
  const setEsp = (v: number) => { setEspS(v); lembrar({ esp: v }); };
  const setBolM = (v: number) => { setBolMS(v); lembrar({ bolM: v }); };
  const setEspM = (v: number) => { setEspMS(v); lembrar({ espM: v }); };
  const setTransp = (v: number) => { setTranspS(v); lembrar({ transp: v }); };
  const setRaio = (v: number) => { setRaioS(v); lembrar({ raio: v }); };
  const [vw, setVw] = useState(0);
  const [vh, setVh] = useState(0);
  const [vista, setVista] = useState({ ox: 0, oy: 0, z: 1 });
  const [atual, setAtual] = useState<Traco | null>(null);
  const [apagando, setApagando] = useState<Traco[] | null>(null);
  const [mira, setMira] = useState<P | null>(null);
  const [sel, setSel] = useState<Sel | null>(null);
  const [desloc, setDesloc] = useState<P>([0, 0]);
  const [edit, setEdit] = useState<Edit>(null);
  const [alca, setAlca] = useState<{ x: number; y: number } | null>(null);   // ✥ em px na tela do quadro
  const [barra, setBarraS] = useState(!barraEscondida);
  const setBarra = (v: boolean) => { barraEscondida = !v; setBarraS(v); };
  // texto 18 A3: as opções da ferramenta começam fechadas e abrem numa janelinha pequena; fecham ao escrever, desenhar ou rolar
  const [opcoes, setOpcoesS] = useState(false);
  const opcoesRef = useRef(false);
  const setOpcoes = (v: boolean) => { if (opcoesRef.current !== v) { opcoesRef.current = v; setOpcoesS(v); } };
  // texto 20 (6): as opções só recolhem quando a pessoa pede (tocar na página, numa parte vazia da barra, de novo na
  // ferramenta ou puxar para cima); escolher cor/espessura ou rolar NÃO recolhe. Vale também na tela cheia.
  const fecharOpcoesRef = useRef(() => {}); fecharOpcoesRef.current = () => { setOpcoes(false); if (onTelaCheia) setOpcoesCheia(false); };
  // texto 18 A1: a folha está "em uso" (a barra pode grudar no topo) depois de tocar nela, ou sempre numa página aberta para escrever
  const ativoRef = useRef(false);
  const tec = useTeclado();
  const tecRef = useRef(tec); tecRef.current = tec;
  // texto 18 A4: escrevendo nesta folha com o teclado aberto: a barra vai para cima do teclado (uma linha só)
  const compacto = !telaCheia && tec.alt > 0 && !!edit;
  const largoTela = useTamanho() !== 'celular';   // texto 18 A7: no computador, as ferramentas numa faixa fina (uma linha)
  const [menu, setMenu] = useState(false);
  const [folhaAberta, setFolhaAberta] = useState(false);
  const [paleta, setPaleta] = useState<{ i: number; marca?: boolean; sel?: boolean } | null>(null);   // texto 26 (E2): sel = pintar o selecionado
  // texto 20 (5): as 4 cores do marca-texto também podem ser trocadas (ficam guardadas neste dispositivo)
  const [coresMarca, setCoresMarcaS] = useState<string[]>(memoria.coresMarca?.length === 4 ? memoria.coresMarca : CORES_MARCA);
  const setCorMarca = (i: number, c: string) => { const n = coresMarca.map((x, j) => (j === i ? c : x)); setCoresMarcaS(n); lembrar({ coresMarca: n }); };
  const [regua, setRegua] = useState<Regua>({ on: false, x: 500, y: 500, a: 0, c: REGUA_C });   // G5
  const [girando, setGirando] = useState(false);
  // G2: opções da ferramenta na tela cheia (cores, tamanhos); no Desenho/assinatura (sem "Sair") ficam abertas como antes
  const [opcoesCheia, setOpcoesCheia] = useState(!onTelaCheia);
  const [canto] = useState<Canto>(cantoBarra);    // G2: canto inicial da barra flutuante
  // texto 11c B9: na tela cheia a barra recolhe (⠿) e anda para onde for arrastada (vale enquanto estiver na tela cheia)
  const [recolhida, setRecolhida] = useState(false);
  const [livre, setLivre] = useState<{ x: number; y: number } | null>(null);
  const tamBarraCheia = useRef({ x: 0, y: 0, w: 0, h: 0 });
  // texto 11b B5 / 11c B10: seleção dentro do texto em edição, estilo para o que for digitado e células escolhidas da tabela
  const [selTxt, setSelTxt] = useState<{ start: number; end: number }>({ start: 0, end: 0 });
  const [selForcada, setSelForcada] = useState<{ start: number; end: number; n: number } | null>(null);
  const pendente = useRef<EstiloTrecho | null>(null);
  const [, setVersaoPend] = useState(0);
  const [celSel, setCelSel] = useState<string[]>([]);
  const linhasTxt = useRef<Record<string, { text: string; x: number; y: number; width: number; height: number }[]>>({});
  const [exportando, setExportando] = useState(false);
  // Guia "Como usar" (texto 08, C1): pontos da barra marcados só nas páginas de Estudos; o guia pode escolher a Caneta
  // (sem guardar como ferramenta preferida) ou abrir o ⋮ antes de destacar
  const guia = aba === 'estudos' && !simples;
  const alvoCaneta = useAlvoGuia('estudos.barra.caneta'), alvoBolinhas = useAlvoGuia('estudos.barra.bolinhas'), alvoEsp = useAlvoGuia('estudos.barra.espessura');
  const alvoDesfazer = useAlvoGuia('estudos.barra.desfazer'), alvoCheia = useAlvoGuia('estudos.barra.telaCheia'), alvoMenu = useAlvoGuia('estudos.barra.menu');
  const alvoDitado = useAlvoGuia('estudos.barra.ditado'), alvoFolha = useAlvoGuia('estudos.menu.folha');
  const seGuia = (a: { ref: (no: View | null) => void }) => (guia ? a.ref : undefined);
  usePreparoTela('quadro:' + (guia ? 'estudos' : ''), acao => {
    if (!guia) return;
    if (acao === 'caneta') { setModoSo('caneta'); setBarra(true); }
    else if (acao === 'menu') setMenu(true);
  });
  const [novaTabela, setNovaTabela] = useState(false);
  const [formas, setFormas] = useState(false);
  const [quebrada, setQuebrada] = useState<Record<string, boolean>>({});   // imagens não encontradas
  const alturas = useRef<Record<string, number>>({});
  const passado = useRef<Q[]>([]);
  const futuro = useRef<Q[]>([]);
  const [, setVersaoHist] = useState(0);
  const s = vw ? (vw / LARG_BASE) * vista.z : 0;

  const corCaneta = tinta(config.bolinhas[bol] ?? '#111827');
  const corTexto = tinta(config.bolinhas[bolTxt] ?? '#111827');
  const corMarca = rgba(coresMarca[bolM] ?? coresMarca[0], Math.max(0.1, Math.min(0.9, 1 - transp / 100)));

  // texto 11c B11: enquanto o dedo está no quadro, a página em volta NÃO rola (Estudos: onRolagem; Diário e outras
  // telas com ScrollArrastavel: a área de rolagem; páginas com a barra fixa: a PaginaRolavel)
  const area = useAreaRolagem();
  const fixaCtx = useContext(BarraFixaCtx);
  const rolagem = (on: boolean) => { onRolagem?.(on); area?.setRolagem(on); fixaCtx?.setRolagem(on); };
  const rolagemRef = useRef(rolagem); rolagemRef.current = rolagem;

  /** Muda o quadro guardando o passo para o ↶ voltar. */
  const onChangeRef = useRef(onChange); onChangeRef.current = onChange;   // os gestos usam sempre o mais novo
  const mudar = (q0: Q, historico = true) => {
    // a primeira coisa escrita numa página nova ou vazia guarda a margem dela (daí em diante nada muda de lugar)
    const v0 = ref.current.valor;
    const q = q0.margem == null && v0.margem == null && !simples && !temConteudo(v0) && temConteudo(q0) ? { ...q0, margem: MARGEM_NOVA } : q0;
    if (historico) { passado.current = [...passado.current.slice(-49), ref.current.valor]; futuro.current = []; setVersaoHist(v => v + 1); }
    onChangeRef.current(q);
  };
  const voltar = () => { const p = passado.current.pop(); if (!p) return; futuro.current.push(ref.current.valor); setVersaoHist(v => v + 1); setSel(null); setEdit(null); onChange(p); };
  const avancar = () => { const f = futuro.current.pop(); if (!f) return; passado.current.push(ref.current.valor); setVersaoHist(v => v + 1); setSel(null); setEdit(null); onChange(f); };

  /** Cresce para baixo e para a direita quando algo chega perto da borda. */
  const crescer = (q: Q, x: number, y: number): Q => ({ ...q,
    larg: x > q.larg - BORDA ? Math.max(q.larg + CRESCE, x + BORDA) : q.larg, alt: y > q.alt - BORDA ? Math.max(q.alt + CRESCE, y + BORDA) : q.alt });

  const ref = useRef({ valor, modo, corCaneta, corMarca, esp, espM, raio, s, vista, edit, sel, alca, vw, vh, folha, regua,
    v0: { ox: 0, oy: 0 }, dedo: [0, 0] as P, auto: null as ReturnType<typeof setInterval> | null, autoDir: [0, 0] as P,
    ponta: null as null | { lado: number; f: P }, ultimoToque: null as null | { id: string; t: number; p: P }, toqueTimer: null as ReturnType<typeof setTimeout> | null,
    trava: 0, reguaIni: null as null | { x: number; y: number; p0: P }, giro: null as null | { a0: number; base: number; m0: P; c0: P; d0: number; comp0: number },
    pts: [] as P[], apag: null as Traco[] | null, ini: [0, 0] as P, iniP: [0, 0] as P, moveu: false, segurou: false,
    timer: null as ReturnType<typeof setTimeout> | null, forma: null as ReturnType<typeof setTimeout> | null, ajeitado: null as string | null,
    gesto: 'nada' as 'nada' | 'desenho' | 'borracha' | 'mover' | 'sel' | 'pinca' | 'toque' | 'regua' | 'reguaPonta', pinca: null as null | { d: number; z: number; cx: number; cy: number; ox: number; oy: number },
    alcaAte: 0, fechouEdit: false });
  Object.assign(ref.current, { valor, modo, corCaneta, corMarca, esp, espM, raio, s, vista, edit, sel, alca, vw, vh, folha, regua });
  /** Topo do texto assentado na linha da folha (G3): vale para os textos novos e os já existentes. */
  const yT = (x: QuadroTexto) => yAssentado(x, ref.current.folha);

  /** Fecha o texto, checklist ou tabela em edição (texto vazio some, menos o "Título"). */
  const fecharEdicao = () => {
    const e = ref.current.edit; if (!e) return;
    setEdit(null);
    const q = ref.current.valor;
    if (e.tipo === 'texto') { const x = q.textos.find(y => y.id === e.id); if (x && !x.texto.trim() && !x.titulo) onChange({ ...q, textos: q.textos.filter(y => y.id !== e.id) }); }
    if (e.tipo === 'check') { const c = q.checks.find(y => y.id === e.id); if (c && !c.itens.some(i => i.text.trim())) onChange({ ...q, checks: q.checks.filter(y => y.id !== e.id) });
      else if (c && c.itens.some(i => !i.text.trim())) onChange({ ...q, checks: q.checks.map(y => (y.id === e.id ? { ...y, itens: y.itens.filter(i => i.text.trim()) } : y)) }); }
  };
  const paraConteudo = (lx: number, ly: number): P => { const r = ref.current; return [r1(lx / r.s + r.vista.ox), r1(ly / r.s + r.vista.oy)]; };

  /** O que está embaixo do dedo: elementos soltos (do mais novo para o mais antigo) e depois o traço mais próximo. */
  const acharEm = (p: P, soElementos = false): Sel | null => {
    const q = ref.current.valor;
    const dentro = (x: { id: string; x: number; y: number; w: number }, hPadrao = 50) =>
      p[0] >= x.x && p[0] <= x.x + x.w && p[1] >= x.y && p[1] <= x.y + (alturas.current[x.id] ?? hPadrao);
    for (const im of [...(q.imagens ?? [])].reverse()) if (p[0] >= im.x && p[0] <= im.x + im.w && p[1] >= im.y && p[1] <= im.y + im.h) return { tipo: 'imagem', id: im.id };
    for (const tb of [...(q.tabelas ?? [])].reverse()) if (dentro(tb, 120)) return { tipo: 'tabela', id: tb.id };
    for (let i = q.checks.length - 1; i >= 0; i--) if (dentro(q.checks[i])) return { tipo: 'check', id: q.checks[i].id };
    for (let i = q.textos.length - 1; i >= 0; i--) if (dentro({ ...q.textos[i], y: yT(q.textos[i]) }, q.textos[i].titulo ? 90 : 50)) return { tipo: 'texto', id: q.textos[i].id };
    if (soElementos) return null;
    let melhor = -1, dist = Infinity;
    q.tracos.forEach((tr, i) => { const d = distTraco(p, pontosDe(tr.d)) - tr.w / 2; if (d < dist) { dist = d; melhor = i; } });
    return melhor >= 0 && dist <= 18 ? { tipo: 'traco', i: melhor } : null;
  };

  /** Abre o texto para escrever; w = palavra selecionada (dois toques, texto 11c B10). */
  const abrirTexto = (id: string, w: { start: number; end: number } | null) => {
    const q = ref.current.valor;
    passado.current = [...passado.current.slice(-49), q]; futuro.current = [];
    pendente.current = null; setSel(null);
    const tx = q.textos.find(y => y.id === id);
    const fim = tx?.texto.length ?? 0;
    setSelTxt(w ?? { start: fim, end: fim });
    setSelForcada(w && w.end > w.start ? { ...w, n: Date.now() } : null);
    setEdit({ tipo: 'texto', id });
  };
  /** Posição (letra) do texto no ponto tocado, pelas linhas medidas do texto na tela. */
  const indiceNoPonto = (x: QuadroTexto, p: P): number => {
    const r = ref.current, linhas = linhasTxt.current[x.id];
    if (!linhas?.length || !r.s) return x.texto.length;
    const rx = (p[0] - x.x) * r.s - 4, ry = (p[1] - yAssentado(x, r.folha)) * r.s;
    let li = linhas.length - 1;
    for (let i = 0; i < linhas.length; i++) if (ry < linhas[i].y + linhas[i].height) { li = i; break; }
    let acc = 0;
    for (let i = 0; i < li; i++) acc += linhas[i].text.length;
    const L = linhas[li], ws = L.text.split('').map(larguraLetra), tot = ws.reduce((a, b) => a + b, 0) || 1, k = L.width / tot;
    let pos = L.x;
    for (let i = 0; i < ws.length; i++) { if (rx < pos + (ws[i] * k) / 2) return Math.min(x.texto.length, acc + i); pos += ws[i] * k; }
    return Math.min(x.texto.length, acc + L.text.replace(/\n$/, '').length);
  };

  /** Aplica o deslocamento ao item escolhido. */
  const deslocar = (q: Q, x: Sel, dx: number, dy: number): Q => {
    if (x.tipo === 'traco') {
      const tr = q.tracos[x.i]; if (!tr) return q;
      const n = moverTraco(tr, dx, dy), cx = caixaTraco(n);
      if (cx.x < 0 || cx.y < 0) return deslocar(q, x, dx - Math.min(0, cx.x), dy - Math.min(0, cx.y));
      return crescer({ ...q, tracos: q.tracos.map((t0, i) => (i === x.i ? n : t0)) }, cx.x + cx.w, cx.y + cx.h);
    }
    const k = chaveLista[x.tipo];
    const lista = (listas(q)[k] as { id: string; x: number; y: number; w: number; h?: number }[]);
    const it = lista.find(y => y.id === x.id); if (!it) return q;
    const nx = Math.max(0, Math.round(it.x + dx));
    const ny = x.tipo === 'texto' ? yAssentado({ ...(it as QuadroTexto), y: Math.max(0, yT(it as QuadroTexto) + dy) }, ref.current.folha) : Math.max(0, Math.round(it.y + dy));
    const nova = lista.map(y => (y.id === x.id ? { ...y, x: nx, y: ny } : y));
    return crescer({ ...q, [k]: nova } as Q, nx + it.w, ny + (it.h ?? alturas.current[x.id] ?? 50));
  };

  /** Anda pela folha (travada à esquerda e em cima; cresce para a direita e para baixo). */
  const andar = (dxPx: number, dyPx: number) => {
    const r = ref.current; if (!r.s) return;
    const v = r.vista;
    const ox = Math.max(0, v.ox - dxPx / r.s), oy = Math.max(0, v.oy - dyPx / r.s);
    r.vista = { ...v, ox, oy };   // já vale para o próximo passo (rolagem automática), antes de redesenhar
    setVista(r.vista);
    const q = r.valor, fimX = ox + r.vw / r.s, fimY = oy + r.vh / r.s;
    if (fimX > q.larg - BORDA / 2 || fimY > q.alt - BORDA / 2) onChangeRef.current(crescer(q, fimX, fimY));
  };

  const pararTimers = () => {
    const r = ref.current;
    if (r.timer) { clearTimeout(r.timer); r.timer = null; }
    if (r.forma) { clearTimeout(r.forma); r.forma = null; }
    pararAuto();
  };
  /** Caixa sendo arrastada (texto 11c B11): deslocamento = dedo + o quanto a folha rolou sozinha desde o começo. */
  const deslocAgora = (): P => { const r = ref.current; return [(r.dedo[0] - r.iniP[0]) / r.s + (r.vista.ox - r.v0.ox), (r.dedo[1] - r.iniP[1]) / r.s + (r.vista.oy - r.v0.oy)]; };
  const pararAuto = () => { const r = ref.current; if (r.auto) { clearInterval(r.auto); r.auto = null; } };
  useEffect(() => () => { const r = ref.current; if (r.auto) clearInterval(r.auto); }, []);   // 0.77: fechar a página no meio do arrasto não deixa o relógio rodando
  /** Perto da borda do quadro, a folha rola sozinha, devagar, enquanto a caixa está sendo arrastada. */
  const rolarNaBorda = (lx: number, ly: number) => {
    const r = ref.current, m = 36;
    const dx = lx < m ? -1 : lx > r.vw - m ? 1 : 0, dy = ly < m ? -1 : ly > r.vh - m ? 1 : 0;
    r.autoDir = [dx, dy];
    if (!dx && !dy) { pararAuto(); return; }
    if (r.auto) return;
    r.auto = setInterval(() => {
      const rr = ref.current;
      if (rr.gesto !== 'sel') { pararAuto(); return; }
      andar(-rr.autoDir[0] * 7, -rr.autoDir[1] * 7);
      setDesloc(deslocAgora());
    }, 32);
  };

  const segurou = (lx: number, ly: number) => {
    const r = ref.current;
    r.timer = null;
    if (r.moveu || r.gesto === 'pinca') return;
    r.segurou = true;
    vibrar();
    setAtual(null); r.pts = [];
    const p = paraConteudo(lx, ly);
    const x = acharEm(p);
    if (x) { r.gesto = 'sel'; r.sel = x; setSel(x); setEdit(null); setDesloc([0, 0]); r.iniP = [lx, ly]; r.dedo = [lx, ly]; r.v0 = { ox: r.vista.ox, oy: r.vista.oy }; return; }
    // lugar vazio: aparece o ✥ para arrastar a folha (some sozinho em alguns segundos)
    setAlca({ x: lx, y: ly }); r.alcaAte = Date.now() + 3500; r.gesto = 'mover'; r.iniP = [lx, ly];
    setTimeout(() => { if (Date.now() >= ref.current.alcaAte - 50) setAlca(null); }, 3600);
  };

  /** Traço sendo desenhado: livre, ou reto encostado na borda da régua (G5). */
  const tracoAgora = (w: number) => {
    const r = ref.current;
    if (!r.trava || !r.pts.length) return caminho(r.pts);
    return caminho([naBorda(r.regua, r.trava, w, r.pts[0]), naBorda(r.regua, r.trava, w, r.pts[r.pts.length - 1])]);
  };

  const pan = useMemo(() => PanResponder.create({
    onStartShouldSetPanResponder: () => true,
    onStartShouldSetPanResponderCapture: () => true,
    onMoveShouldSetPanResponder: () => true,
    onMoveShouldSetPanResponderCapture: () => true,
    onPanResponderTerminationRequest: () => false,
    onPanResponderGrant: e => {
      const r = ref.current; if (!r.s) return;
      rolagemRef.current(false);
      ativoRef.current = true; fecharOpcoesRef.current();   // texto 18: tocou na folha = folha em uso; as opções fecham
      const { locationX: lx, locationY: ly } = e.nativeEvent;
      const toques = e.nativeEvent.touches?.length ?? 1;
      r.moveu = false; r.segurou = false; r.ini = [lx, ly]; r.iniP = [lx, ly]; r.ajeitado = null;
      pararTimers();
      if (toques >= 2) return;
      const p = paraConteudo(lx, ly);
      // tocou no ✥: arrasta a folha
      if (r.alca && Math.hypot(lx - r.alca.x, ly - r.alca.y) < 36) { r.gesto = 'mover'; r.alcaAte = Date.now() + 3500; return; }
      if (r.alca) setAlca(null);
      // texto 11b B6: puxar uma das pontas estica a régua (a outra ponta fica parada)
      const lado = r.regua.on ? pontaRegua(r.regua, p) : 0;
      if (lado) { r.gesto = 'reguaPonta'; r.ponta = { lado, f: daRegua(r.regua, [-lado * r.regua.c / 2, 0]) }; r.giro = null; return; }
      // G5: um dedo em cima da régua move a régua (qualquer ferramenta)
      if (r.regua.on && naRegua(r.regua, p)) { r.gesto = 'regua'; r.reguaIni = { x: r.regua.x, y: r.regua.y, p0: p }; r.giro = null; return; }
      r.fechouEdit = !!r.edit;
      if (r.edit) fecharEdicao();   // tocar fora fecha a edição (e não cria outro texto neste toque)
      r.timer = setTimeout(() => segurou(lx, ly), ESPERA_SEGURAR);
      if (r.modo === 'caneta' || r.modo === 'marca') {
        r.gesto = 'desenho'; r.pts = [p];
        const w = r.modo === 'marca' ? r.espM : r.esp, c = r.modo === 'marca' ? r.corMarca : r.corCaneta;
        r.trava = r.regua.on ? ladoRegua(r.regua, p) : 0;   // começou encostado na régua: o traço gruda na borda
        setAtual({ d: tracoAgora(w), cor: c, w });
      } else if (r.modo === 'borracha') {
        r.gesto = 'borracha'; r.pts = [p]; r.apag = apagarPedaco(r.valor.tracos, p, r.raio); setApagando(r.apag); setMira(p);
      } else if (r.modo === 'mover') r.gesto = 'mover';
      else if (r.modo === 'selecionar') {
        const x = acharEm(p);
        r.sel = x; setSel(x); setDesloc([0, 0]); r.dedo = [lx, ly]; r.v0 = { ox: r.vista.ox, oy: r.vista.oy };
        r.gesto = x ? 'sel' : 'toque';
      } else r.gesto = 'toque';
    },
    onPanResponderMove: e => {
      const r = ref.current; if (!r.s) return;
      const ts = e.nativeEvent.touches ?? [];
      // dois dedos: zoom (pinça) e andar pela folha
      if (ts.length >= 2) {
        pararTimers();
        if (r.gesto === 'reguaPonta') { r.gesto = 'regua'; r.ponta = null; }   // segundo dedo: volta a girar a régua
        if (r.gesto === 'desenho') { r.pts = []; setAtual(null); }
        if (r.gesto === 'borracha') { r.apag = null; setApagando(null); setMira(null); }
        const [a, b] = [ts[0], ts[1]];
        // G5: dois dedos na régua giram a régua (com encaixe em 30°, 45°, 60°, 90°) e também a levam junto
        const g = r.regua;
        if (g.on && r.gesto !== 'pinca' && (r.gesto === 'regua' || naRegua(g, paraConteudo(a.locationX, a.locationY)) || naRegua(g, paraConteudo(b.locationX, b.locationY)))) {
          r.gesto = 'regua'; r.moveu = true;
          const ang = (Math.atan2(b.locationY - a.locationY, b.locationX - a.locationX) * 180) / Math.PI;
          const m = paraConteudo((a.locationX + b.locationX) / 2, (a.locationY + b.locationY) / 2);
          const dd = Math.hypot(a.locationX - b.locationX, a.locationY - b.locationY) || 1;
          if (!r.giro) { r.giro = { a0: ang, base: g.a, m0: m, c0: [g.x, g.y], d0: dd, comp0: g.c }; setGirando(true); return; }
          let d = ang - r.giro.a0; while (d > 180) d -= 360; while (d < -180) d += 360;
          // texto 20 (4): afastar os dois dedos estica e aproximar encolhe, dos dois lados (o centro fica entre os dedos);
          // uma folga pequena evita que o girar mude o tamanho sem querer
          const k = dd / r.giro.d0;
          const kk = Math.abs(k - 1) < 0.06 ? 1 : k > 1 ? k - 0.06 : k + 0.06;
          const comp = Math.max(REGUA_MIN, Math.min(REGUA_MAX, r.giro.comp0 * kk));
          setRegua({ ...g, a: encaixar(r.giro.base + d), c: comp, x: r.giro.c0[0] + m[0] - r.giro.m0[0], y: r.giro.c0[1] + m[1] - r.giro.m0[1] });
          return;
        }
        const d = Math.hypot(a.locationX - b.locationX, a.locationY - b.locationY) || 1;
        const cx = (a.locationX + b.locationX) / 2, cy = (a.locationY + b.locationY) / 2;
        if (r.gesto !== 'pinca' || !r.pinca) { r.gesto = 'pinca'; r.pinca = { d, z: r.vista.z, cx, cy, ox: r.vista.ox, oy: r.vista.oy }; r.moveu = true; return; }
        const pc = r.pinca, base = r.vw / LARG_BASE;
        const z = Math.max(0.5, Math.min(3, pc.z * (d / pc.d)));
        const s0 = base * pc.z, s1 = base * z;
        // o ponto entre os dedos fica no mesmo lugar da folha
        const cxC = pc.cx / s0 + pc.ox, cyC = pc.cy / s0 + pc.oy;
        setVista({ z, ox: Math.max(0, cxC - cx / s1), oy: Math.max(0, cyC - cy / s1) });
        return;
      }
      const { locationX: lx, locationY: ly } = e.nativeEvent;
      if (!r.moveu && Math.hypot(lx - r.ini[0], ly - r.ini[1]) > 8) { r.moveu = true; if (r.timer) { clearTimeout(r.timer); r.timer = null; } }
      const p = paraConteudo(lx, ly);
      if (r.gesto === 'reguaPonta' && r.ponta) {
        const g = r.regua, dir: P = [Math.cos(rad(g.a)), Math.sin(rad(g.a))], lado = r.ponta.lado, f = r.ponta.f;
        const c = Math.max(REGUA_MIN, Math.min(REGUA_MAX, ((p[0] - f[0]) * dir[0] + (p[1] - f[1]) * dir[1]) * lado));
        setRegua({ ...g, c: r1(c), x: r1(f[0] + dir[0] * lado * c / 2), y: r1(f[1] + dir[1] * lado * c / 2) });
        return;
      }
      if (r.gesto === 'regua') {
        // tirou um dos dois dedos: continua movendo com o que ficou, sem pular
        if (r.giro) { r.giro = null; setGirando(false); r.reguaIni = { x: r.regua.x, y: r.regua.y, p0: p }; return; }
        const ri = r.reguaIni; if (ri) setRegua({ ...r.regua, x: r1(ri.x + p[0] - ri.p0[0]), y: r1(ri.y + p[1] - ri.p0[1]) });
        return;
      }
      if (r.gesto === 'mover') {
        const dx = lx - r.iniP[0], dy = ly - r.iniP[1]; r.iniP = [lx, ly];
        andar(dx, dy);
        if (ref.current.alca) { setAlca({ x: lx, y: ly }); r.alcaAte = Date.now() + 3500; }
        return;
      }
      if (r.gesto === 'sel') {
        if (!r.sel) return;
        r.dedo = [lx, ly];
        setDesloc(deslocAgora());
        if (r.moveu) rolarNaBorda(lx, ly);
        return;
      }
      if (r.gesto === 'borracha') {
        const u = r.pts[r.pts.length - 1];
        const n = u ? Math.max(1, Math.ceil(Math.hypot(p[0] - u[0], p[1] - u[1]) / Math.max(4, r.raio / 2))) : 1;
        let tr = r.apag ?? r.valor.tracos;
        for (let k = 1; k <= n; k++) tr = apagarPedaco(tr, u ? [u[0] + ((p[0] - u[0]) * k) / n, u[1] + ((p[1] - u[1]) * k) / n] : p, r.raio);
        r.apag = tr; r.pts.push(p); setApagando(tr); setMira(p);
        return;
      }
      if (r.gesto !== 'desenho') return;
      const u = r.pts[r.pts.length - 1];
      if (u && Math.hypot(p[0] - u[0], p[1] - u[1]) < 3) return;
      r.pts.push(p); r.ajeitado = null;
      const w = r.modo === 'marca' ? r.espM : r.esp, c = r.modo === 'marca' ? r.corMarca : r.corCaneta;
      setAtual({ d: tracoAgora(w), cor: c, w });
      if (r.trava) return;   // grudado na régua já é uma reta
      // segurou o dedo parado no fim do traço: vira a forma perfeita
      if (r.forma) clearTimeout(r.forma);
      r.forma = setTimeout(() => {
        const rr = ref.current; rr.forma = null;
        if (rr.gesto !== 'desenho' || rr.pts.length < 6) return;
        const d = ajeitarForma(rr.pts);
        if (d) { rr.ajeitado = d; vibrar(); setAtual({ d, cor: c, w }); }
      }, ESPERA_FORMA);
    },
    onPanResponderRelease: e => {
      const r = ref.current;
      pararTimers();
      rolagemRef.current(true);
      const gesto = r.gesto; r.gesto = 'nada'; r.pinca = null;
      const { locationX: lx, locationY: ly } = e.nativeEvent;
      const q = r.valor;
      if (gesto === 'regua' || gesto === 'reguaPonta') { r.giro = null; r.reguaIni = null; r.ponta = null; setGirando(false); return; }
      if (gesto === 'pinca' || !r.s) return;
      if (gesto === 'desenho') {
        const pts = r.pts; r.pts = []; setAtual(null);
        if (r.segurou || !pts.length) return;
        const w = r.modo === 'marca' ? r.espM : r.esp, c = r.modo === 'marca' ? r.corMarca : r.corCaneta;
        const fim = r.trava ? [naBorda(r.regua, r.trava, w, pts[0]), naBorda(r.regua, r.trava, w, pts[pts.length - 1])] : pts;
        const d = r.trava ? caminho(fim) : r.ajeitado ?? caminho(pts);
        r.trava = 0;
        const mx = Math.max(...fim.map(p => p[0])), my = Math.max(...fim.map(p => p[1]));
        mudar(crescer({ ...q, tracos: [...q.tracos, { d, cor: c, w }] }, mx, my));
        return;
      }
      if (gesto === 'borracha') {
        const tr = r.apag; r.apag = null; setApagando(null); setMira(null);
        if (tr && (tr.length !== q.tracos.length || tr.some((x, i) => x !== q.tracos[i]))) mudar({ ...q, tracos: tr });
        return;
      }
      if (gesto === 'sel') {
        const x = r.sel; r.dedo = [lx, ly];
        const [dx, dy] = deslocAgora(); setDesloc([0, 0]);
        if (x && r.moveu) mudar(deslocar(q, x, dx, dy));
        return;
      }
      if (gesto === 'mover' && r.moveu) return;
      if (r.segurou) return;
      // toque simples
      const p = paraConteudo(lx, ly);
      const x = acharEm(p, true);
      if (x?.tipo === 'check') {
        const ck = q.checks.find(c => c.id === x.id)!;
        // tocou no quadradinho: marca ou desmarca
        if (p[0] < ck.x + 46) {
          const linha = Math.floor((p[1] - ck.y - 6) / ((alturas.current[ck.id] ?? 50) / Math.max(1, ck.itens.length)));
          const it = ck.itens[Math.max(0, Math.min(ck.itens.length - 1, linha))];
          if (it) { mudar({ ...q, checks: q.checks.map(c => (c.id === ck.id ? { ...c, itens: c.itens.map(y => (y.id === it.id ? { ...y, done: !y.done } : y)) } : c)) }); return; }
        }
      }
      if (x?.tipo === 'texto') {
        // texto 11c B10: um toque abre o texto para escrever; dois toques seguidos na mesma caixa selecionam a palavra
        // (com as alças do sistema para puxar e pegar mais palavras ou linhas)
        const ant = r.ultimoToque;
        if (ant && ant.id === x.id && Date.now() - ant.t < 320 && r.toqueTimer) {
          clearTimeout(r.toqueTimer); r.toqueTimer = null; r.ultimoToque = null;
          const tx = q.textos.find(y => y.id === x.id);
          const w = tx ? palavraEm(tx.texto, indiceNoPonto(tx, ant.p)) : null;
          abrirTexto(x.id, w);
          return;
        }
        if (r.toqueTimer) clearTimeout(r.toqueTimer);
        r.ultimoToque = { id: x.id, t: Date.now(), p };
        r.toqueTimer = setTimeout(() => { ref.current.toqueTimer = null; abrirTexto(x.id, null); }, 280);
        setSel(null);
        return;
      }
      if (x && x.tipo !== 'imagem' && x.tipo !== 'traco') { passado.current = [...passado.current.slice(-49), q]; futuro.current = []; setEdit({ tipo: x.tipo, id: x.id }); setSel(null); setCelSel([]); return; }
      if (x?.tipo === 'imagem') { setSel(x); return; }
      setSel(null);
      if (r.modo === 'texto' && !r.fechouEdit) {
        const w = Math.max(300, Math.min(700, q.larg - p[0] - 20));
        const id = uid();
        const novo: QuadroTexto = { id, x: p[0], y: 0, w, texto: '', cor: corTextoRef.current, ...(estiloRef.current), tam: estiloRef.current.tam ?? TAM_NORMAL, tamLivre: true };
        // G3: a linha onde tocou vira a linha do texto (a base das letras assenta nela)
        novo.y = yAssentado({ ...novo, y: Math.max(0, p[1] - medidasTexto(novo).l * 0.75) }, r.folha);
        mudar(crescer({ ...q, textos: [...q.textos, novo] }, p[0] + w, p[1] + 200));
        pendente.current = null; setSelTxt({ start: 0, end: 0 }); setSelForcada(null);
        setEdit({ tipo: 'texto', id });
      }
    },
    onPanResponderTerminate: () => {
      const r = ref.current; pararTimers(); rolagemRef.current(true);
      r.gesto = 'nada'; r.pts = []; r.apag = null; r.giro = null; r.reguaIni = null; r.ponta = null; r.trava = 0; setGirando(false); setAtual(null); setApagando(null); setMira(null); setDesloc([0, 0]);
    },
  }), []); // eslint-disable-line react-hooks/exhaustive-deps

  // estilo do próximo texto (cor, negrito, itálico, sublinhado, tamanho)
  const [estilo, setEstilo] = useState<{ b?: boolean; i?: boolean; u?: boolean; tam?: number }>({});
  const corTextoRef = useRef(corTexto); corTextoRef.current = corTexto;
  const estiloRef = useRef(estilo); estiloRef.current = estilo;
  const txtEdit = edit?.tipo === 'texto' ? valor.textos.find(x => x.id === edit.id) : null;
  const tabEdit = edit?.tipo === 'tabela' ? (valor.tabelas ?? []).find(x => x.id === edit.id) : null;
  /** Estilo dos botões N I S, cor e tamanho: o do trecho selecionado (ou do ponto do cursor), o "a digitar" ou o do próximo texto. */
  const estiloAtual = (): { tam: number; b: boolean; i: boolean; u: boolean } => {
    if (txtEdit) {
      const e = estiloNoPonto(txtEdit, Math.min(selTxt.start, selTxt.end));
      return { ...e, ...(selTxt.end === selTxt.start ? pendente.current ?? {} : {}) } as { tam: number; b: boolean; i: boolean; u: boolean };
    }
    if (tabEdit) { const k = celSel[0]; return { tam: (k && tabEdit.tams?.[k]) || TAM_CELULA, b: false, i: false, u: false }; }
    return { tam: estilo.tam ?? TAM_NORMAL, b: !!estilo.b, i: !!estilo.i, u: !!estilo.u };
  };
  /** texto 11b B5 / 11c B10: com um trecho selecionado, o estilo vale SÓ para o trecho; sem seleção, vale para o que
   *  for digitado a seguir (numa caixa ainda vazia, para a caixa). Na tabela, o tamanho vale para as células escolhidas.
   *  Sem nada em edição, vale para o próximo texto. */
  const mudarEstilo = (k: 'b' | 'i' | 'u' | 'tam' | 'cor', v: any) => {
    const q = ref.current.valor;
    if (edit?.tipo === 'texto') {
      const x = q.textos.find(y => y.id === edit.id);
      if (x) {
        const a = Math.min(selTxt.start, selTxt.end), z = Math.max(selTxt.start, selTxt.end);
        if (z > a) {
          mudar({ ...q, textos: q.textos.map(y => (y.id === x.id ? { ...y, trechos: aplicarEstilo(y.trechos, y.texto.length, a, z, { [k]: v }) } : y)) }, false);
          setSelForcada({ start: selTxt.start, end: selTxt.end, n: Date.now() });   // a seleção continua no lugar
        } else if (!x.texto.length) {
          mudar({ ...q, textos: q.textos.map(y => (y.id === x.id ? { ...y, [k]: v, ...(k === 'tam' ? { tamLivre: true } : {}) } : y)) }, false);
        } else { pendente.current = { ...pendente.current, [k]: v }; setVersaoPend(n => n + 1); }
      }
    } else if (edit?.tipo === 'tabela' && k === 'tam') {
      const tb = (q.tabelas ?? []).find(y => y.id === edit.id);
      if (tb && celSel.length) {
        const tams = { ...(tb.tams ?? {}) }; celSel.forEach(c => { tams[c] = v; });
        mudar({ ...q, tabelas: (q.tabelas ?? []).map(y => (y.id === tb.id ? { ...y, tams } : y)) }, false);
      }
    }
    if (k !== 'cor' && edit?.tipo !== 'tabela') setEstilo(e => ({ ...e, [k]: v }));
  };

  // web: rodinha do mouse anda pela folha; Ctrl + rodinha dá zoom
  const caixaRef = useRef<View | null>(null);
  useEffect(() => {
    if (Platform.OS !== 'web') return;
    const el = caixaRef.current as unknown as HTMLElement | null;
    if (!el?.addEventListener) return;
    const roda = (ev: WheelEvent) => {
      ev.preventDefault();
      if (ev.ctrlKey) setVista(v => ({ ...v, z: Math.max(0.5, Math.min(3, v.z * (ev.deltaY < 0 ? 1.1 : 0.9))) }));
      else andar(-ev.deltaX, -ev.deltaY);
    };
    el.addEventListener('wheel', roda, { passive: false });
    return () => el.removeEventListener('wheel', roda);
  }, [vw]); // eslint-disable-line react-hooks/exhaustive-deps

  // ─── ações do menu ───
  const centro = (): P => [r1(vista.ox + (vw / (s || 1)) / 2), r1(vista.oy + (vh / (s || 1)) / 2)];
  const inserirCheck = () => {
    const [x, y] = centro(); const id = uid();
    mudar(crescer({ ...valor, checks: [...valor.checks, { id, x: Math.max(0, x - 250), y: Math.max(0, y - 60), w: 500, itens: [{ id: uid(), text: '', done: false }] }] }, x + 300, y + 200));
    setEdit({ tipo: 'check', id });
  };
  const inserirTabela = (lin: number, col: number) => {
    const [x, y] = centro(); const id = uid();
    const w = Math.min(900, col * 220);
    mudar(crescer({ ...valor, tabelas: [...(valor.tabelas ?? []), { id, x: Math.max(0, x - w / 2), y: Math.max(0, y - 80), w, linhas: Array.from({ length: lin }, () => Array(col).fill('')) }] }, x + w, y + lin * 90));
    setEdit({ tipo: 'tabela', id });
  };
  const inserirForma = (f: Forma) => {
    const [x, y] = centro();
    const reta = f === 'linha' || f === 'seta';
    let d = caminhoForma(f, x - 120, y - (reta ? 0 : 100), 240, reta ? 0 : 200);
    if (regua.on) {
      // G5: com a régua ligada, a forma nasce encostada na borda de cima da régua, no mesmo ângulo dela
      const h = reta ? 0 : 200, base = -(REGUA_L / 2 + esp / 2);
      const local = caminhoForma(f, -120, base - h, 240, h);
      d = caminhoPartes(pontosDe(local).map(pts => pts.map(q => daRegua(regua, q))));
    }
    mudar(crescer({ ...valor, tracos: [...valor.tracos, { d, cor: corCaneta, w: esp }] }, x + 200, y + 200));
    setSel({ tipo: 'traco', i: valor.tracos.length });
  };
  // 0.71 (texto 09 C1/C2): menu Fotos · Tirar foto · Arquivos, aberto só depois do menu ⋮ fechar de todo (iPhone)
  const anexo = useAnexar({ cor });
  const inserirImagem = async () => {
    try {
      const r0 = await anexo.anexar({ tipo: 'imagem', titulo: t('Inserir imagem') });
      if (!r0) return;
      const r = { arq: r0.anexo, aviso: r0.aviso };
      const src = fonteImagem(r.arq);
      const por = (w: number, h: number) => {
        const [x, y] = centro(); const largura = 600, altura = Math.round((largura * h) / (w || 1)) || 450;
        mudar(crescer({ ...ref.current.valor, imagens: [...(ref.current.valor.imagens ?? []), { id: uid(), x: Math.max(0, x - largura / 2), y: Math.max(0, y - altura / 2), w: largura, h: altura, arq: r.arq }] }, x + largura, y + altura));
      };
      if (src) Image.getSize(src, por, () => por(4, 3)); else por(4, 3);
      if (r.aviso) Alert.alert(t('Imagem adicionada'), r.aviso);
    } catch (e: any) { Alert.alert(t('Não foi possível'), String(e?.message ?? e)); }
  };
  const limpar = () => Alert.alert(t('Limpar a folha inteira?'), t('Apaga tudo o que está no quadro desta página.'), [
    { text: t('Cancelar'), style: 'cancel' },
    { text: t('Limpar'), style: 'destructive', onPress: () => { setSel(null); setEdit(null); mudar({ ...valor, tracos: [], textos: valor.textos.filter(x => x.titulo).map(x => ({ ...x, texto: '' })), checks: [], tabelas: [], imagens: [] }); } }]);
  const exportar = async (tipo: 'pdf' | 'imagem') => {
    // a foto (imagem) leva a folha inteira desenhada por baixo; espera ela aparecer antes de tirar
    if (tipo === 'imagem') { setExportando(true); await new Promise(r => setTimeout(r, 120)); }
    try { await exportarQuadro(valor, { tipo, nome: nome || t('Página'), fundo, escura, folha, conteudo: conteudoRef.current }); }
    catch (e: any) { Alert.alert(t('Não foi possível exportar'), String(e?.message ?? e)); }
    finally { setExportando(false); }
  };
  const conteudoRef = useRef<View | null>(null);

  // seleção: duplicar, excluir, cor, maior/menor
  const [maisSel, setMaisSel] = useState(false);
  /** A cor do item selecionado (traço ou texto). */
  const corDoSel = () => {
    const v = ref.current.valor; if (!sel) return '';
    if (sel.tipo === 'traco') return v.tracos[sel.i]?.cor ?? '';
    if (sel.tipo === 'texto') return v.textos.find(x => x.id === sel.id)?.cor ?? '';
    return '';
  };
  /** Pinta o selecionado com a cor escolhida na paleta (o texto inteiro, inclusive os trechos que tinham outra cor). */
  const pintarSel = (c: string) => {
    const v = ref.current.valor; if (!sel) return;
    if (sel.tipo === 'traco') mudar({ ...v, tracos: v.tracos.map((tr, i) => (i === sel.i ? { ...tr, cor: c } : tr)) });
    if (sel.tipo === 'texto') mudar({ ...v, textos: v.textos.map(x => (x.id === sel.id ? { ...x, cor: tinta(c), ...(x.trechos ? { trechos: x.trechos.map(z => ({ ...z, cor: undefined })) } : {}) } : x)) });
  };
  const selAcoes = (compacta = false) => {
    if (!sel) return null;
    const n = deslocar(valor, sel, 0, 0);
    const duplicar = () => {
      if (sel.tipo === 'traco') { const tr = n.tracos[sel.i]; if (tr) { mudar({ ...n, tracos: [...n.tracos, moverTraco(tr, 30, 30)] }); setSel({ tipo: 'traco', i: n.tracos.length }); } return; }
      const k = chaveLista[sel.tipo], lista = listas(n)[k] as any[];
      const x = lista.find(y => y.id === sel.id); if (!x) return;
      const novoId = uid();
      const copia = { ...JSON.parse(JSON.stringify(x)), id: novoId, x: x.x + 30, y: x.y + 30, ...(x.titulo ? { titulo: false } : {}) };
      if (copia.itens) copia.itens = copia.itens.map((i: Item) => ({ ...i, id: uid() }));
      mudar({ ...n, [k]: [...lista, copia] } as Q);
      setSel({ tipo: sel.tipo, id: novoId });
    };
    const excluir = () => {
      if (sel.tipo === 'traco') mudar({ ...valor, tracos: valor.tracos.filter((_, i) => i !== sel.i) });
      else { const k = chaveLista[sel.tipo]; mudar({ ...valor, [k]: (listas(valor)[k] as any[]).filter(y => y.id !== sel.id) } as Q); }
      setSel(null);
    };
    // texto 26 (E2): CAUSA do "Cor atual não faz nada": ela pintava o item com a cor que já estava na caneta (a mesma do
    // traço, na maioria das vezes), então nada mudava. Agora mostra a cor do item e abre a paleta; a cor escolhida pinta o item.
    const pintar = () => { setMaisSel(false); setPaleta({ i: 0, sel: true }); };
    const tamanho = (k: number) => {
      if (sel.tipo === 'imagem') mudar({ ...valor, imagens: (valor.imagens ?? []).map(x => (x.id === sel.id ? { ...x, w: Math.max(80, Math.round(x.w * k)), h: Math.max(60, Math.round(x.h * k)) } : x)) });
      // G4: A− / A+ andam um tamanho (Pequena · Normal · Grande · Enorme), para o texto continuar nas linhas
      if (sel.tipo === 'texto') mudar({ ...valor, textos: valor.textos.map(x => (x.id !== sel.id ? x : x.titulo
        ? { ...x, tam: Math.max(24, Math.min(120, Math.round((x.tam ?? TAM_TITULO) * k))) }
        : { ...x, tam: NIVEIS_TEXTO[Math.max(0, Math.min(NIVEIS_TEXTO.length - 1, nivelDoTexto(x.tam) + (k > 1 ? 1 : -1)))].tam })) });
      if (sel.tipo === 'tabela' || sel.tipo === 'check') { const kk = chaveLista[sel.tipo]; mudar({ ...valor, [kk]: (listas(valor)[kk] as any[]).map(x => (x.id === sel.id ? { ...x, w: Math.max(200, Math.round(x.w * k)) } : x)) } as Q); }
    };
    // texto 20 (1): na tela cheia, uma linha compacta ao lado do "Sair"; A−, A+ e a cor vão para o "⋯"
    if (compacta) {
      return (
        <View style={{ alignItems: 'flex-end', gap: 6, flexShrink: 1 }}>
          {maisSel && (
            <View style={{ flexDirection: 'row', gap: 6, padding: 6, backgroundColor: alpha(C.card, 0.97), borderRadius: 12, borderWidth: 1, borderColor: C.line }}>
              {(sel.tipo === 'traco' || sel.tipo === 'texto') && <Chip small cor={cor} on={false} label={'🎨 ' + t('Cor atual')} onPress={pintar} />}
              {sel.tipo !== 'traco' && <Chip small cor={cor} on={false} label="A−" onPress={() => tamanho(0.85)} />}
              {sel.tipo !== 'traco' && <Chip small cor={cor} on={false} label="A+" onPress={() => tamanho(1.18)} />}
            </View>
          )}
          {/* texto 26 (E1): CAUSA do "Pronto" escondido: a faixa tinha os nomes (Duplicar, Excluir) e rolava de lado, ficando
              maior que o espaço ao lado do "Sair". Agora Duplicar e Excluir ficam só com o ícone e a faixa cabe inteira. */}
          <View style={{ flexDirection: 'row', gap: 4, padding: 4, backgroundColor: alpha(C.card, 0.97), borderRadius: 12, borderWidth: 1, borderColor: C.line }}>
            {([['⧉', t('Duplicar'), duplicar], ['🗑', t('Excluir'), excluir], ['⋯', t('Mais'), () => setMaisSel(v => !v)]] as [string, string, () => void][]).map(([ic, nome, fn]) => (
              <Pressable key={ic} onPress={fn} accessibilityRole="button" accessibilityLabel={nome} hitSlop={4}
                style={{ minWidth: 36, height: 32, borderRadius: 10, alignItems: 'center', justifyContent: 'center', backgroundColor: ic === '⋯' && maisSel ? alpha(cor, 0.25) : C.card2 }}>
                <Text style={{ color: C.text, fontSize: 15, fontWeight: '800' }}>{ic}</Text>
              </Pressable>))}
            <Chip small cor={cor} on label={t('Pronto')} onPress={() => { setMaisSel(false); setSel(null); }} />
          </View>
        </View>
      );
    }
    return (
      <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 6, padding: 6, backgroundColor: alpha(C.card, 0.96), borderRadius: 12, borderWidth: 1, borderColor: C.line }}>
        <Chip small cor={cor} on={false} label={'⧉ ' + t('Duplicar')} onPress={duplicar} />
        {(sel.tipo === 'traco' || sel.tipo === 'texto') && <Chip small cor={cor} on={false} label={'🎨 ' + t('Cor atual')} onPress={pintar} />}
        {sel.tipo !== 'traco' && <Chip small cor={cor} on={false} label="A−" onPress={() => tamanho(0.85)} />}
        {sel.tipo !== 'traco' && <Chip small cor={cor} on={false} label="A+" onPress={() => tamanho(1.18)} />}
        <Chip small cor={cor} on={false} label={'🗑 ' + t('Excluir')} onPress={excluir} />
        <Chip small cor={cor} on label={t('Pronto')} onPress={() => setSel(null)} />
      </View>
    );
  };

  // ─── Régua (G5) ───
  const alternarRegua = () => setRegua(g => {
    if (g.on) return { ...g, on: false };
    const [x, y] = centro();
    return { on: true, x, y, a: g.a, c: g.c };
  });
  const desenhoRegua = useMemo(() => {
    if (!regua.on) return null;
    const C0 = Math.round(regua.c / 10) * 10, c2 = regua.c / 2, l2 = REGUA_L / 2;
    let d = '';
    for (let i = 0; i <= C0; i += 10) {
      const x = i - c2, h = i % 100 === 0 ? 26 : i % 50 === 0 ? 18 : 10;
      d += `M${x} ${-l2}L${x} ${-l2 + h}M${x} ${l2}L${x} ${l2 - h}`;
    }
    const tintaR = escura ? '#E5E7EB' : '#1F2937';
    return (
      <G transform={`translate(${regua.x} ${regua.y}) rotate(${regua.a})`}>
        <Rect x={-c2} y={-l2} width={regua.c} height={REGUA_L} rx={10} fill={alpha(escura ? '#94A3B8' : '#CBD5E1', 0.55)} stroke={cor} strokeWidth={girando ? 4 : 2.5} />
        <Path d={d} stroke={tintaR} strokeWidth={2} />
        {Array.from({ length: Math.floor(regua.c / 100) + 1 }, (_, k) => k).filter(k => k > 0 && k * 100 < regua.c && Math.abs(k * 100 - c2) > 60).map(k => (
          <SvgText key={k} x={k * 100 - c2} y={-l2 + 48} fontSize={18} fill={tintaR} textAnchor="middle">{String(k)}</SvgText>
        ))}
        {/* B6: pegadores nas pontas (puxar estica a régua) */}
        {[-1, 1].map(l => (
          <G key={l}>
            <Rect x={l < 0 ? -c2 + 6 : c2 - 34} y={-22} width={28} height={44} rx={8} fill={alpha(cor, 0.85)} />
            <Path d={`M${l * (c2 - 20) - 5} -10 L${l * (c2 - 20) - 5} 10 M${l * (c2 - 20) + 5} -10 L${l * (c2 - 20) + 5} 10`} stroke="#fff" strokeWidth={3} strokeLinecap="round" />
          </G>
        ))}
      </G>
    );
  }, [regua.on, regua.x, regua.y, regua.a, regua.c, girando, cor, escura]);

  const W = valor.larg * s, H = valor.alt * s;
  const px = (x: number) => (x - vista.ox) * s, py = (y: number) => (y - vista.oy) * s;
  const movendo = (tipo: string, id: string) => sel && sel.tipo === tipo && (sel as any).id === id;
  const dsl = (tipo: string, id: string): P => (movendo(tipo, id) ? desloc : [0, 0]);
  const linhaSel = (tipo: string, id: string) => (movendo(tipo, id) || (edit?.tipo === tipo && edit.id === id)) ? { borderWidth: 2, borderColor: cor, borderStyle: 'dashed' as const, borderRadius: 8 } : null;
  // ─── barra de ferramentas (texto 11b B1–B5; texto 11c B9) ───
  const bolinhas = (selI: number, onSel: (i: number) => void, cores: string[] = config.bolinhas, daFolha = true) => (
    <View ref={daFolha ? seGuia(alvoBolinhas) : undefined} collapsable={false} style={{ flexDirection: 'row', gap: 10, alignItems: 'center' }}>
      {cores.map((c, i) => (
        // texto 20 (5): tocar na bolinha que já está escolhida abre a paleta grande (segurar também abre)
        <Pressable key={i} onPress={() => (selI === i ? setPaleta({ i, marca: !daFolha }) : onSel(i))} onLongPress={() => setPaleta({ i, marca: !daFolha })} delayLongPress={350} hitSlop={4}
          accessibilityRole="button" accessibilityState={{ selected: selI === i }} accessibilityLabel={t('Cor {n}', { n: i + 1 })}
          style={{ width: 36, height: 36, borderRadius: 18, backgroundColor: daFolha ? tinta(c) : c, borderWidth: selI === i ? 3 : 1, borderColor: selI === i ? cor : C.line }} />
      ))}
    </View>
  );
  const temOpcoes = (m: Modo) => m === 'texto' || m === 'caneta' || m === 'borracha' || m === 'marca';
  const ferramentaBtn = (m: Modo, rot: string, ic: NomeIcone) => {
    const on = modo === m;
    return (
      <BotaoBarra key={m} icone={ic} rotulo={rot} on={on} cor={cor} min={68} alvo={m === 'caneta' ? seGuia(alvoCaneta) : undefined}
        seta={telaCheia && on && temOpcoes(m) ? (opcoesCheia ? '▴' : '▾') : undefined}
        // G2: na tela cheia, tocar de novo na ferramenta escolhida mostra ou esconde as opções dela
        // texto 18 A3: fora da tela cheia, tocar na ferramenta abre a janelinha de opções; tocar de novo fecha
        // texto 20 (6c): um toque escolhe a ferramenta E abre as opções; tocar de novo na ativa fecha (sem desligar)
        onPress={() => {
          if (telaCheia) { if (on) setOpcoesCheia(v => temOpcoes(m) && !v); else { setModo(m); setOpcoesCheia(temOpcoes(m)); } return; }
          if (on) setOpcoes(temOpcoes(m) && !opcoesRef.current); else { setModo(m); setOpcoes(temOpcoes(m)); } }} />
    );
  };
  const linha1 = [
    !simples && ferramentaBtn('texto', t('Texto'), 'texto'),
    ferramentaBtn('caneta', t('Caneta'), 'caneta'),
    !simples && ferramentaBtn('marca', t('Marca-texto'), 'marca'),
    ferramentaBtn('borracha', t('Borracha'), 'borracha'),
  ];
  const linha2 = [
    <BotaoBarra key="esc" icone="esconder" rotulo={t('Esconder')} cor={cor} onPress={() => { if (telaCheia) setRecolhida(true); else { setOpcoes(false); setBarra(false); } }} rotuloAcessivel={t('Esconder a barra')} />,
    !simples && <BotaoBarra key="reg" icone="regua" rotulo={t('Régua')} cor={cor} on={regua.on} onPress={alternarRegua} />,
    <BotaoBarra key="volt" icone="voltar" rotulo={t('Voltar')} cor={cor} desab={!passado.current.length} onPress={voltar} alvo={seGuia(alvoDesfazer)} />,
    <BotaoBarra key="ref" icone="refazer" rotulo={t('Refazer')} cor={cor} desab={!futuro.current.length} onPress={avancar} />,
    !telaCheia && !!onTelaCheia && <BotaoBarra key="cheia" icone="telaCheia" rotulo={t('Tela cheia')} cor={cor} onPress={onTelaCheia} alvo={seGuia(alvoCheia)} />,
    !simples && <BotaoBarra key="mais" icone="mais" rotulo={t('Mais')} cor={cor} onPress={() => setMenu(true)} alvo={seGuia(alvoMenu)} rotuloAcessivel={t('Mais ferramentas')} />,
  ];
  // as duas linhas cabem na tela; em telas pequenas rolam de lado (de propósito)
  // texto 18 A7: no computador (tela larga), uma linha só: as ferramentas ficam numa faixa fina no topo
  // texto 20 (6e-3): tocar numa parte livre (vazia) da barra recolhe as opções
  const linhas = (
    <Pressable onPress={() => fecharOpcoesRef.current()} accessible={false} style={{ cursor: 'auto' } as any}>
      {largoTela && !telaCheia ? (
        <ScrollView horizontal showsHorizontalScrollIndicator={false} keyboardShouldPersistTaps="handled" contentContainerStyle={{ flexGrow: 1, gap: 6, alignItems: 'center' }}>
          {linha1}{linha2}{props.extra}
        </ScrollView>
      ) : (
        <View style={{ gap: 6 }}>
          <ScrollView horizontal showsHorizontalScrollIndicator={false} keyboardShouldPersistTaps="handled" contentContainerStyle={{ flexGrow: 1, gap: 6 }}>{linha1}</ScrollView>
          <ScrollView horizontal showsHorizontalScrollIndicator={false} keyboardShouldPersistTaps="handled" contentContainerStyle={{ flexGrow: 1, gap: 6, alignItems: 'center' }}>
            {linha2}{props.extra}
          </ScrollView>
        </View>
      )}
    </Pressable>
  );
  // texto 20 (6e-1): puxar o dedo para CIMA sobre as opções recolhe
  const puxarFecha = useMemo(() => PanResponder.create({
    onMoveShouldSetPanResponderCapture: (_e, g) => g.dy < -14 && Math.abs(g.dy) > Math.abs(g.dx) * 1.5,
    onPanResponderTerminationRequest: () => false,
    onPanResponderRelease: (_e, g) => { if (g.dy < -20) fecharOpcoesRef.current(); },
  }), []);
  const modoExtra = modo === 'selecionar' || modo === 'mover' ? (
    <Chip small cor={cor} on label={modo === 'selecionar' ? '🔲 ' + t('Selecionar') : '✋ ' + t('Mover')} onPress={() => setModo(modo)} />
  ) : null;
  const ditado = (
    <View ref={seGuia(alvoDitado)} collapsable={false}>
    <Ditado cor={cor} onTexto={tx => {
        // ditado: escreve no texto em edição ou cria um texto novo no meio da tela
        const q = ref.current.valor;
        if (edit?.tipo === 'texto') return mudar({ ...q, textos: q.textos.map(x => (x.id === edit.id ? { ...x, texto: (x.texto ? x.texto + ' ' : '') + tx } : x)) });
        const [x, y] = centro(); const id = uid();
        mudar(crescer({ ...q, textos: [...q.textos, { id, x: Math.max(0, x - 300), y: yAssentado({ y }, folha), w: 600, texto: tx, cor: corTexto }] }, x + 300, y + 100));
      }} />
    </View>
  );
  // B3/B4: cada opção com número, 4 bolinhas de tamanho que andam junto com a barrinha, e prévia ao vivo
  const pxBase = (vw || 360) / LARG_BASE;   // medida na tela com zoom 100%
  const DOT_PX = [6, 10, 16, 24];
  const secao = (rot: string, num: string | number, filhos: ReactNode, alvo?: (no: View | null) => void) => (
    <View ref={alvo} collapsable={false} style={{ gap: 6 }}>
      <View style={{ flexDirection: 'row', alignItems: 'center' }}>
        <Text style={{ color: C.sub, fontSize: 13, flex: 1 }}>{rot}</Text>
        <Text style={{ color: C.text, fontSize: 14, fontWeight: '800' }}>{num}</Text>
      </View>
      {filhos}
    </View>
  );
  const quatro = (vals: number[], atual: number, onSel: (v: number) => void, desenho: (i: number, on: boolean) => ReactNode, rotulo: (v: number) => string) => {
    const ativo = maisPerto(vals, atual);
    return (
      <View style={{ flexDirection: 'row', gap: 8 }}>
        {vals.map((v, i) => (
          <Pressable key={i} onPress={() => onSel(v)} accessibilityRole="button" accessibilityLabel={rotulo(v)} accessibilityState={{ selected: i === ativo }}
            style={{ width: 52, height: 48, borderRadius: 12, borderWidth: 1, alignItems: 'center', justifyContent: 'center',
              borderColor: i === ativo ? cor : C.line, backgroundColor: i === ativo ? alpha(cor, 0.18) : C.card2 }}>
            {desenho(i, i === ativo)}
          </Pressable>
        ))}
      </View>
    );
  };
  const ponto = (i: number, on: boolean) => <View style={{ width: DOT_PX[i], height: DOT_PX[i], borderRadius: DOT_PX[i] / 2, backgroundColor: on ? cor : C.text }} />;
  const previa = (filho: ReactNode) => (
    <View style={{ height: 56, borderRadius: 12, backgroundColor: C.card2, alignItems: 'center', justifyContent: 'center', overflow: 'hidden' }}>{filho}</View>
  );
  const espessura = (vals: number[], faixa: [number, number], v: number, set: (n: number) => void, rot = t('Espessura'), alvo?: (no: View | null) => void) => secao(rot, v, (
    <View style={{ gap: 4 }}>
      {quatro(vals, v, set, ponto, n => rot + ' ' + n)}
      <Deslizante valor={v} min={faixa[0]} max={faixa[1]} cor={cor} onMudar={set} rotulo={rot} />
    </View>
  ), alvo);
  const ea = estiloAtual();
  const tabSemCelula = !!tabEdit && !celSel.length;
  const sub = modo === 'caneta' ? (
    <View style={{ gap: 10 }}>
      <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 10, alignItems: 'center' }}>{bolinhas(bol, setBol)}{ditado}</View>
      {espessura(DOTS_CANETA, FAIXA_CANETA, esp, setEsp, t('Espessura'), seGuia(alvoEsp))}
      {previa(<View style={{ width: '70%', height: Math.max(1, esp * pxBase), borderRadius: 99, backgroundColor: corCaneta }} />)}
    </View>
  ) : modo === 'marca' ? (
    <View style={{ gap: 10 }}>
      <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 10, alignItems: 'center' }}>{bolinhas(bolM, setBolM, coresMarca, false)}{ditado}</View>
      {espessura(DOTS_MARCA, FAIXA_MARCA, espM, setEspM)}
      {secao(t('Transparência'), transp + '%', <Deslizante valor={transp} min={10} max={90} cor={cor} onMudar={setTransp} rotulo={t('Transparência')} />)}
      {previa(<View style={{ width: '70%', height: Math.max(2, espM * pxBase), backgroundColor: corMarca }} />)}
    </View>
  ) : modo === 'borracha' ? (
    <View style={{ gap: 10 }}>
      {espessura(DOTS_BORRACHA, FAIXA_BORRACHA, raio, setRaio, t('Tamanho da borracha'))}
      {previa(<View style={{ width: Math.max(6, raio * 2 * pxBase), height: Math.max(6, raio * 2 * pxBase), borderRadius: 999, borderWidth: 2, borderColor: C.sub }} />)}
    </View>
  ) : modo === 'texto' ? (
    <View style={{ gap: 10 }}>
      <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 8, alignItems: 'center' }}>
        {bolinhas(bolTxt, i => { setBolTxt(i); mudarEstilo('cor', tinta(config.bolinhas[i])); })}
        {(['b', 'i', 'u'] as const).map(k => {
          const on = ea[k];
          return (
            <Pressable key={k} onPress={() => mudarEstilo(k, !on)} accessibilityRole="button" accessibilityState={{ selected: on }}
              style={{ width: 44, height: 44, borderRadius: 12, alignItems: 'center', justifyContent: 'center', backgroundColor: on ? cor : C.card2, borderWidth: 1, borderColor: on ? cor : C.line }}>
              <Text style={{ color: on ? '#fff' : C.text, fontSize: 17, fontWeight: k === 'b' ? '900' : '500', fontStyle: k === 'i' ? 'italic' : 'normal', textDecorationLine: k === 'u' ? 'underline' : 'none' }}>{k === 'b' ? 'N' : k === 'i' ? 'I' : 'S'}</Text>
            </Pressable>
          );
        })}
        {ditado}
      </View>
      {secao(t('Tamanho da letra'), numeroDoTam(ea.tam), (
        <View style={{ gap: 4 }}>
          {quatro(TAMANHOS_A, ea.tam, v => mudarEstilo('tam', v), (i, on) => <Text style={{ color: on ? cor : C.text, fontSize: [13, 17, 22, 28][i], fontWeight: '700' }}>A</Text>, v => t('Tamanho da letra') + ' ' + numeroDoTam(v))}
          <Deslizante valor={numeroDoTam(ea.tam)} min={numeroDoTam(TAM_MIN)} max={numeroDoTam(TAM_MAX)} cor={cor} onMudar={n => mudarEstilo('tam', n * 2)} rotulo={t('Tamanho da letra')} />
        </View>
      ))}
      {tabSemCelula ? <Text style={{ color: C.sub, fontSize: 12 }}>{t('Toque numa célula da tabela (ou em "Escolher células") para mudar o tamanho da letra dela.')}</Text>
        : previa(<Text allowFontScaling={false} numberOfLines={1} style={{ color: corTexto, fontSize: Math.max(8, ea.tam * pxBase), fontWeight: ea.b ? '800' : '400', fontStyle: ea.i ? 'italic' : 'normal', textDecorationLine: ea.u ? 'underline' : 'none' }}>{t('Texto de exemplo')}</Text>)}
    </View>
  ) : null;

  // ─── texto 18 A3: janelinha de opções (pequena, altura fixa: no máximo ~15% da tela) ───
  // linha 1: cores + N I S + microfone; linha 2: os 4 tamanhos + a barrinha; com o teclado fechado, a prévia (de tamanho limitado)
  const PEQ = 30;
  const bolinhasPeq = (selI: number, onSel: (i: number) => void, cores: string[] = config.bolinhas, daFolha = true) => cores.map((c, i) => (
    <Pressable key={'c' + i} onPress={() => (selI === i ? setPaleta({ i, marca: !daFolha }) : onSel(i))} onLongPress={() => setPaleta({ i, marca: !daFolha })} delayLongPress={350} hitSlop={3}
      accessibilityRole="button" accessibilityState={{ selected: selI === i }} accessibilityLabel={t('Cor {n}', { n: i + 1 })}
      style={{ width: PEQ, height: PEQ, borderRadius: PEQ / 2, backgroundColor: daFolha ? tinta(c) : c, borderWidth: selI === i ? 3 : 1, borderColor: selI === i ? cor : C.line }} />
  ));
  const linhaPeq = (filhos: ReactNode) => (
    <ScrollView horizontal showsHorizontalScrollIndicator={false} keyboardShouldPersistTaps="handled" contentContainerStyle={{ gap: 8, alignItems: 'center', paddingRight: 4 }}>{filhos}</ScrollView>
  );
  const quatroPeq = (vals: number[], atual: number, onSel: (v: number) => void, desenho: (i: number, on: boolean) => ReactNode, rotulo: (v: number) => string) => {
    const ativo = maisPerto(vals, atual);
    return vals.map((v, i) => (
      <Pressable key={'q' + i} onPress={() => onSel(v)} accessibilityRole="button" accessibilityLabel={rotulo(v)} accessibilityState={{ selected: i === ativo }}
        style={{ width: 40, height: 34, borderRadius: 10, borderWidth: 1, alignItems: 'center', justifyContent: 'center',
          borderColor: i === ativo ? cor : C.line, backgroundColor: i === ativo ? alpha(cor, 0.18) : C.card2 }}>
        {desenho(i, i === ativo)}
      </Pressable>
    ));
  };
  const tamanhoPeq = (vals: number[], faixa: [number, number], v: number, set: (n: number) => void, rot: string, desenho = ponto) => (
    <View style={{ flexDirection: 'row', alignItems: 'center', gap: 6 }}>
      {quatroPeq(vals, v, set, desenho, n => rot + ' ' + n)}
      <View style={{ flex: 1, minWidth: 90 }}><Deslizante valor={v} min={faixa[0]} max={faixa[1]} cor={cor} onMudar={set} rotulo={rot} /></View>
      <Text style={{ color: C.text, fontSize: 13, fontWeight: '800', minWidth: 24, textAlign: 'right' }}>{v}</Text>
    </View>
  );
  // prévia de altura fixa (a altura da janelinha nunca muda com o tamanho escolhido); some com o teclado aberto
  const previaPeq = (filho: ReactNode) => (tec.alt > 0 ? null : (
    <View style={{ height: 30, borderRadius: 10, backgroundColor: C.card2, alignItems: 'center', justifyContent: 'center', overflow: 'hidden' }}>{filho}</View>
  ));
  const subPeq = modo === 'caneta' ? (<>
    {linhaPeq(<>{bolinhasPeq(bol, setBol)}{ditado}</>)}
    {tamanhoPeq(DOTS_CANETA, FAIXA_CANETA, esp, setEsp, t('Espessura'))}
    {previaPeq(<View style={{ width: '70%', height: Math.min(14, Math.max(1, esp * pxBase)), borderRadius: 99, backgroundColor: corCaneta }} />)}
  </>) : modo === 'marca' ? (<>
    {linhaPeq(<>{bolinhasPeq(bolM, setBolM, coresMarca, false)}{ditado}</>)}
    {tamanhoPeq(DOTS_MARCA, FAIXA_MARCA, espM, setEspM, t('Espessura'))}
    <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
      <Text style={{ color: C.sub, fontSize: 12 }}>{t('Transparência')}</Text>
      <View style={{ flex: 1 }}><Deslizante valor={transp} min={10} max={90} cor={cor} onMudar={setTransp} rotulo={t('Transparência')} /></View>
      <Text style={{ color: C.text, fontSize: 13, fontWeight: '800' }}>{transp}%</Text>
    </View>
  </>) : modo === 'borracha' ? (
    tamanhoPeq(DOTS_BORRACHA, FAIXA_BORRACHA, raio, setRaio, t('Tamanho da borracha'))
  ) : modo === 'texto' ? (<>
    {linhaPeq(<>
      {bolinhasPeq(bolTxt, i => { setBolTxt(i); mudarEstilo('cor', tinta(config.bolinhas[i])); })}
      {(['b', 'i', 'u'] as const).map(k => {
        const on = ea[k];
        return (
          <Pressable key={k} onPress={() => mudarEstilo(k, !on)} accessibilityRole="button" accessibilityState={{ selected: on }}
            style={{ width: 36, height: 34, borderRadius: 10, alignItems: 'center', justifyContent: 'center', backgroundColor: on ? cor : C.card2, borderWidth: 1, borderColor: on ? cor : C.line }}>
            <Text style={{ color: on ? '#fff' : C.text, fontSize: 16, fontWeight: k === 'b' ? '900' : '500', fontStyle: k === 'i' ? 'italic' : 'normal', textDecorationLine: k === 'u' ? 'underline' : 'none' }}>{k === 'b' ? 'N' : k === 'i' ? 'I' : 'S'}</Text>
          </Pressable>
        );
      })}
      {ditado}
    </>)}
    {tabSemCelula ? <Text style={{ color: C.sub, fontSize: 12 }}>{t('Toque numa célula da tabela (ou em "Escolher células") para mudar o tamanho da letra dela.')}</Text> : (
      <View style={{ flexDirection: 'row', alignItems: 'center', gap: 6 }}>
        {quatroPeq(TAMANHOS_A, ea.tam, v => mudarEstilo('tam', v), (i, on) => <Text style={{ color: on ? cor : C.text, fontSize: [12, 15, 18, 22][i], fontWeight: '700' }}>A</Text>, v => t('Tamanho da letra') + ' ' + numeroDoTam(v))}
        <View style={{ flex: 1, minWidth: 90 }}><Deslizante valor={numeroDoTam(ea.tam)} min={numeroDoTam(TAM_MIN)} max={numeroDoTam(TAM_MAX)} cor={cor} onMudar={n => mudarEstilo('tam', n * 2)} rotulo={t('Tamanho da letra')} /></View>
        <Text style={{ color: C.text, fontSize: 13, fontWeight: '800', minWidth: 24, textAlign: 'right' }}>{numeroDoTam(ea.tam)}</Text>
      </View>
    )}
    {!tabSemCelula && previaPeq(<Text allowFontScaling={false} numberOfLines={1} style={{ color: corTexto, fontSize: Math.min(18, Math.max(10, ea.tam * pxBase)), fontWeight: ea.b ? '800' : '400', fontStyle: ea.i ? 'italic' : 'normal', textDecorationLine: ea.u ? 'underline' : 'none' }}>{t('Texto de exemplo')}</Text>)}
  </>) : null;
  const altTela = Dimensions.get('window').height;
  const janelinha = opcoes && !!subPeq ? (
    <View {...puxarFecha.panHandlers} style={{ borderWidth: 1, borderColor: alpha(cor, 0.5), borderRadius: 14, backgroundColor: C.card, padding: 6 }}>
      <ScrollView style={{ maxHeight: Math.max(146, Math.round(altTela * 0.15)) }} keyboardShouldPersistTaps="handled" nestedScrollEnabled contentContainerStyle={{ gap: 6 }}>{subPeq}</ScrollView>
    </View>
  ) : null;

  // B9: barra da tela cheia: os pontinhos ⠿ recolhem (toque) e arrastam (segurar e puxar) a barra; recolhida, fica um botãozinho
  const arraste = useRef(new Animated.ValueXY()).current;
  // texto 22: dois dedos sobre a barra a REDUZEM (nunca passa do tamanho original; mínimo ainda fácil de tocar).
  // Esconder no 🛠 ou sair/entrar na tela cheia volta ao tamanho original; a posição continua a de antes.
  const escala = useRef(new Animated.Value(1)).current;
  const escalaRef = useRef(1);
  const livreRef = useRef(livre); livreRef.current = livre;
  const pinca = useRef<{ d0: number; s0: number } | null>(null);
  const distDedos = (e: { nativeEvent: { touches?: { pageX: number; pageY: number }[] } }) => {
    const tq = e.nativeEvent.touches || [];
    return tq.length >= 2 ? Math.hypot(tq[0].pageX - tq[1].pageX, tq[0].pageY - tq[1].pageY) : 0;
  };
  const doisDedos = (e: { nativeEvent: { touches?: unknown[] } }) => (e.nativeEvent.touches?.length ?? 0) >= 2;
  // ponto fixo da redução: o canto onde a barra está presa (assim ela encolhe "para dentro" do lugar onde ficou)
  const origemEscala = livre ? 'top left' : `${canto[0] === 't' ? 'top' : 'bottom'} ${canto[1] === 'e' ? 'left' : 'right'}`;
  useEffect(() => { escalaRef.current = 1; escala.setValue(1); pinca.current = null; }, [recolhida, telaCheia]); // eslint-disable-line react-hooks/exhaustive-deps
  // ao abrir de novo no tamanho original, se não couber inteira onde estava, ajusta para dentro da tela
  useEffect(() => {
    if (recolhida || !livre) return;
    const b = tamBarraCheia.current, r = ref.current;
    if (!b.w || !r.vw) return;
    const x = Math.max(0, Math.min(r.vw - b.w, livre.x)), y = Math.max(0, Math.min(r.vh - b.h, livre.y));
    if (x !== livre.x || y !== livre.y) setLivre({ x, y });
  }, [recolhida]); // eslint-disable-line react-hooks/exhaustive-deps
  const pincaBarra = {
    onStartShouldSetResponderCapture: doisDedos,
    onMoveShouldSetResponderCapture: doisDedos,
    onResponderGrant: (e: any) => { pinca.current = { d0: distDedos(e), s0: escalaRef.current }; },
    onResponderMove: (e: any) => {
      const p = pinca.current, d = distDedos(e);
      if (!p || !d) return;
      if (!p.d0) { p.d0 = d; p.s0 = escalaRef.current; return; }
      const n = Math.max(ESCALA_BARRA_MIN, Math.min(1, p.s0 * d / p.d0));
      escalaRef.current = n; escala.setValue(n);
    },
    onResponderRelease: () => { pinca.current = null; },
    onResponderTerminate: () => { pinca.current = null; },
    onResponderTerminationRequest: () => false,
  };
  const panBarra = useMemo(() => PanResponder.create({
    onStartShouldSetPanResponder: () => true,
    onMoveShouldSetPanResponder: () => true,
    onPanResponderTerminationRequest: e => (e.nativeEvent.touches?.length ?? 0) >= 2,   // texto 22: o 2º dedo passa para a redução
    onPanResponderMove: Animated.event([null, { dx: arraste.x, dy: arraste.y }], { useNativeDriver: false }),
    onPanResponderRelease: (_e, g) => {
      if (Math.hypot(g.dx, g.dy) < 8) { arraste.setValue({ x: 0, y: 0 }); setRecolhida(true); return; }
      const b = tamBarraCheia.current, r = ref.current;
      // texto 22: reduzida, a barra ocupa w*s x h*s; presa no canto direito/de baixo, o desenho começa mais para dentro
      const sc = escalaRef.current, w = b.w * sc, h = b.h * sc;
      const ox = !livreRef.current && canto[1] !== 'e' ? b.w - w : 0, oy = !livreRef.current && canto[0] !== 't' ? b.h - h : 0;
      setLivre({ x: Math.max(0, Math.min(r.vw - w, b.x + ox + g.dx)), y: Math.max(0, Math.min(r.vh - h, b.y + oy + g.dy)) });
      requestAnimationFrame(() => arraste.setValue({ x: 0, y: 0 }));
    },
    onPanResponderTerminate: () => arraste.setValue({ x: 0, y: 0 }),
  }), []); // eslint-disable-line react-hooks/exhaustive-deps
  const emCima = livre ? livre.y + tamBarraCheia.current.h / 2 < vh / 2 : canto[0] === 't';
  const sombra = { shadowColor: '#000', shadowOpacity: 0.18, shadowRadius: 8, shadowOffset: { width: 0, height: 3 }, elevation: 6 };
  const posCheia = livre ? { left: livre.x, top: livre.y } : { ...(canto[0] === 't' ? { top: 8 } : { bottom: 8 + ins.bottom }), ...(canto[1] === 'e' ? { left: 8 } : { right: 8 }) };

  const barraCheia = recolhida ? (
    <Pressable onPress={() => setRecolhida(false)} accessibilityRole="button" accessibilityLabel={t('Mostrar a barra')}
      style={[{ position: 'absolute', zIndex: 20, width: 48, height: 48, borderRadius: 24, alignItems: 'center', justifyContent: 'center', backgroundColor: C.card, borderWidth: 2, borderColor: cor, ...sombra }, posCheia]}>
      <Text style={{ fontSize: 20 }}>🛠️</Text>
    </Pressable>
  ) : (
    <Animated.View onLayout={e => { const l = e.nativeEvent.layout; tamBarraCheia.current = { x: l.x, y: l.y, w: l.width, h: l.height }; }}
      {...pincaBarra}
      style={[{ position: 'absolute', zIndex: 20, width: Math.max(200, Math.min((vw || 360) - 16, 520)), padding: 6, gap: 8, borderRadius: 20,
        backgroundColor: alpha(C.card, 0.97), borderWidth: 1, borderColor: C.line, ...sombra }, posCheia,
        { transformOrigin: origemEscala, transform: [...arraste.getTranslateTransform(), { scale: escala }] } as any]}>
      <View style={{ flexDirection: 'row', gap: 6 }}>
        <View {...panBarra.panHandlers} accessible accessibilityRole="button" accessibilityLabel={t('Recolher ou arrastar a barra')} onAccessibilityTap={() => setRecolhida(true)}
          style={{ width: 44, minHeight: 44, alignSelf: 'stretch', alignItems: 'center', justifyContent: 'center', borderRadius: 12, backgroundColor: C.card2 }}>
          <Text style={{ color: C.sub, fontSize: 22 }}>⠿</Text>
        </View>
        <View style={{ flex: 1 }}>{linhas}</View>
      </View>
      {!!modoExtra && <View style={{ flexDirection: 'row' }}>{modoExtra}</View>}
      {/* texto 20 (6f): na tela cheia das páginas, a mesma janelinha pequena de opções (no Desenho/assinatura, as opções completas) */}
      {opcoesCheia && !!sub && (onTelaCheia
        ? <View {...puxarFecha.panHandlers}><ScrollView style={{ maxHeight: Math.max(146, Math.round(altTela * 0.15)) }} keyboardShouldPersistTaps="handled" nestedScrollEnabled contentContainerStyle={{ gap: 6 }}>{subPeq}</ScrollView></View>
        : <ScrollView style={{ maxHeight: Math.max(160, vh * 0.45) }} keyboardShouldPersistTaps="handled">{sub}</ScrollView>)}
    </Animated.View>
  );
  // texto 18 A6: escondida = some tudo (barra e opções); fica só a etiqueta pequena "🛠 Ferramentas"
  const etiqueta = (
    <Pressable onPress={() => setBarra(true)} accessibilityRole="button" accessibilityLabel={t('Mostrar as ferramentas')}
      style={{ alignSelf: 'flex-start', flexDirection: 'row', alignItems: 'center', minHeight: 36, paddingHorizontal: 14, borderRadius: 18,
        backgroundColor: alpha(cor, 0.18), borderWidth: 1, borderColor: cor }}>
      <Text style={{ color: C.text, fontSize: 14, fontWeight: '800' }}>🛠 {t('Ferramentas')}</Text>
    </Pressable>
  );
  const barraNormal = !barra ? etiqueta : (
    <View style={{ gap: 8 }}>
      {linhas}
      {!!modoExtra && <View style={{ flexDirection: 'row' }}>{modoExtra}</View>}
      {!compacto && janelinha}
    </View>
  );
  // texto 18 A4: com o teclado aberto (escrevendo nesta folha), uma linha só de ícones logo acima do teclado, e o
  // "⌄ Fechar teclado" sempre à direita (B4)
  const icPeq = (k: string, ic: NomeIcone, rot: string, onPress: () => void, on = false, desab = false) => (
    <Pressable key={k} onPress={onPress} disabled={desab} accessibilityRole="button" accessibilityLabel={rot} accessibilityState={{ selected: on, disabled: desab }}
      style={({ pressed }) => ({ width: 42, height: 40, borderRadius: 10, alignItems: 'center', justifyContent: 'center', borderWidth: 1,
        borderColor: on ? cor : C.line, backgroundColor: on ? alpha(cor, 0.18) : pressed ? alpha(cor, 0.12) : C.card2, opacity: desab ? 0.4 : 1 })}>
      <Icone nome={ic} cor={on ? cor : C.text} tam={20} />
    </Pressable>
  );
  const toqueFerramenta = (m: Modo) => () => { if (modo === m) setOpcoes(temOpcoes(m) && !opcoesRef.current); else { setModo(m); setOpcoes(temOpcoes(m)); } };
  const fecharTeclado = (
    <Pressable onPress={() => { setOpcoes(false); Keyboard.dismiss(); }} accessibilityRole="button" accessibilityLabel={t('Fechar teclado')}
      style={{ height: 40, paddingHorizontal: 10, borderRadius: 10, alignItems: 'center', justifyContent: 'center', backgroundColor: cor }}>
      <Text style={{ color: '#fff', fontWeight: '800', fontSize: 13 }}>⌄ {t('Fechar teclado')}</Text>
    </Pressable>
  );
  const barraTeclado = (
    <View style={{ gap: 6 }}>
      {barra && janelinha}
      <View style={{ flexDirection: 'row', alignItems: 'center', gap: 6 }}>
        <View style={{ flex: 1 }}>
          {!barra ? etiqueta : (
            <ScrollView horizontal showsHorizontalScrollIndicator={false} keyboardShouldPersistTaps="always" contentContainerStyle={{ gap: 6, alignItems: 'center' }}>
              {!simples && icPeq('texto', 'texto', t('Texto'), toqueFerramenta('texto'), modo === 'texto')}
              {icPeq('caneta', 'caneta', t('Caneta'), toqueFerramenta('caneta'), modo === 'caneta')}
              {!simples && icPeq('marca', 'marca', t('Marca-texto'), toqueFerramenta('marca'), modo === 'marca')}
              {icPeq('borracha', 'borracha', t('Borracha'), toqueFerramenta('borracha'), modo === 'borracha')}
              {icPeq('voltar', 'voltar', t('Voltar'), voltar, false, !passado.current.length)}
              {icPeq('refazer', 'refazer', t('Refazer'), avancar, false, !futuro.current.length)}
              {icPeq('esc', 'esconder', t('Esconder a barra'), () => { setOpcoes(false); setBarra(false); })}
              {!simples && icPeq('mais', 'mais', t('Mais ferramentas'), () => setMenu(true))}
            </ScrollView>
          )}
        </View>
        {fecharTeclado}
      </View>
    </View>
  );
  // G2: botão claro "Sair" da tela cheia (no lado oposto ao da barra, para não ficar embaixo dela)
  // texto 20 (1 e 2): no mesmo canto, a faixa compacta da seleção e o "⬆ Topo" (depois de descer meia tela)
  const subirAoTopo = () => {
    const passo = () => {
      const r = ref.current; const oy = r.vista.oy;
      if (oy <= 1) { r.vista = { ...r.vista, oy: 0 }; setVista(r.vista); return; }
      r.vista = { ...r.vista, oy: oy * 0.72 }; setVista(r.vista); requestAnimationFrame(passo);
    };
    passo();
  };
  const verTopo = s > 0 && vista.oy * s > vh / 2;
  const sairBtn = telaCheia ? (
    <View pointerEvents="box-none" style={[{ position: 'absolute', left: 8, right: 8, zIndex: 21, flexDirection: 'row', justifyContent: 'flex-end', alignItems: 'flex-end', gap: 6 },
      emCima ? { bottom: 8 + ins.bottom } : { top: 8 }]}>
      {!!sel && selAcoes(true)}
      {verTopo && (
        <Pressable onPress={subirAoTopo} accessibilityRole="button" accessibilityLabel={t('Voltar ao topo')}
          style={{ minHeight: 44, paddingHorizontal: 12, borderRadius: 22, alignItems: 'center', justifyContent: 'center', backgroundColor: alpha(C.card, 0.97), borderWidth: 1, borderColor: C.line, ...sombra }}>
          <Text style={{ color: C.text, fontSize: 14, fontWeight: '800' }}>⬆ {t('Topo')}</Text>
        </Pressable>
      )}
      {!!onTelaCheia && (
        <Pressable ref={seGuia(alvoCheia)} collapsable={false} onPress={onTelaCheia} accessibilityRole="button" accessibilityLabel={t('Sair')}
          style={{ minHeight: 44, paddingHorizontal: 18, borderRadius: 22, alignItems: 'center', justifyContent: 'center', backgroundColor: cor, ...sombra }}>
          <Text style={{ color: '#fff', fontSize: 15, fontWeight: '800' }}>✕ {t('Sair')}</Text>
        </Pressable>
      )}
    </View>
  ) : null;

  // B2: barra fixa logo abaixo do título enquanto a página rola (quando a página usa a PaginaRolavel)
  // texto 18 A1: só gruda com a folha em uso (tocou nela ou é uma página aberta para escrever) e enquanto a folha está
  // na tela; rolando para além da folha (fotos, vídeos, resumo...), a barra volta para o lugar dela.
  const barraNo = useRef<View | null>(null);
  const [fixa, setFixa] = useState(false);
  const fixaRef = useRef(false);
  const [altBarra, setAltBarra] = useState(0);
  const altBarraRef = useRef(0); altBarraRef.current = altBarra;
  const idFixa = useRef(uid()).current;
  useEffect(() => { if (fixaCtx?.editor) ativoRef.current = true; }, [fixaCtx]);
  useEffect(() => { if (edit) ativoRef.current = true; }, [edit]);
  useEffect(() => {
    if (!fixaCtx || telaCheia) return;
    const soltar = () => { if (fixaRef.current) { fixaRef.current = false; setFixa(false); } };
    const checar = () => {
      const topo = fixaCtx.topo.current;
      if (topo == null) return;
      barraNo.current?.measureInWindow((_x, yb) => {
        caixaRef.current?.measureInWindow((_x2, yc, _w, hc) => {
          const altJanela = Dimensions.get('window').height;
          // a folha saiu da tela: deixa de estar "em uso" (menos nas páginas abertas para escrever)
          if ((yc + hc < topo || yc > altJanela) && !fixaCtx.editor && !ref.current.edit) ativoRef.current = false;
          const deve = ativoRef.current && yb < topo && yc + hc > topo + altBarraRef.current + 60;
          if (deve && !fixaRef.current) { fixaRef.current = true; setFixa(true); }
          else if (!deve) soltar();
        });
      });
    };
    const sai = fixaCtx.assinar(checar);
    return () => { sai(); };   // texto 20 (6d): rolar a página NÃO recolhe as opções
  }, [fixaCtx, telaCheia]);
  useEffect(() => { if (fixaCtx && !telaCheia) fixaCtx.mostrar(idFixa, fixa && !compacto ? barraNormal : null); });
  useEffect(() => { if (fixaCtx && !telaCheia) fixaCtx.mostrarRodape(idFixa, compacto ? barraTeclado : null); });
  useEffect(() => () => { fixaCtx?.mostrar(idFixa, null); fixaCtx?.mostrarRodape(idFixa, null); rolagemRef.current(true); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // texto 20 (7): folha até o pé da tela; a conta usa o lugar da barra na página e a altura da página
  const [altAuto, setAltAuto] = useState(0);
  const medirAltura = () => {
    if (!props.encherTela || !fixaCtx || telaCheia) return;
    const H = fixaCtx.altVisivel.current, yRaiz = fixaCtx.yRaiz.current;
    if (!H || yRaiz == null) return;
    barraNo.current?.measureInWindow((_x, yb) => {
      const noConteudo = yb - yRaiz + fixaCtx.y.current;
      const h = Math.round(H - noConteudo - altBarraRef.current - 8 - 16);
      setAltAuto(a => { const n = Math.max(Math.round(H / 3), h); return Math.abs(n - a) > 2 ? n : a; });
    });
  };
  useEffect(() => { const t0 = setTimeout(medirAltura, 60); return () => clearTimeout(t0); }, [altBarra, barra, fixa]); // eslint-disable-line react-hooks/exhaustive-deps
  const alturaEf = props.encherTela && altAuto ? Math.max(altura ?? 0, altAuto) : altura;

  // texto 18 A5: a linha em que se escreve fica sempre à vista: dentro da folha (a folha anda) e na tela (a página rola
  // até ela ficar acima do teclado e da linha de ferramentas)
  const editNo = useRef<View | null>(null);
  const seguirCursor = () => {
    const no = editNo.current, r = ref.current;
    if (!no || !r.s || !r.edit) return;
    no.measureInWindow((_x, ye, _w, he) => {
      caixaRef.current?.measureInWindow((_x2, yc, _w2, hc) => {
        const fundoNaFolha = ye + he - yc;
        if (fundoNaFolha > hc - 8 && he < hc - 24) {
          const vv = { ...r.vista, oy: r.vista.oy + (fundoNaFolha - (hc - 8)) / r.s };
          r.vista = vv; setVista(vv);
          setTimeout(seguirCursor, 80);
          return;
        }
        const tq = tecRef.current;
        if (!tq.alt) return;
        const limite = tq.topo - (fixaCtx?.altRodape.current ?? 0) - 10;
        const fundo = Math.min(ye + he, yc + hc);
        if (fundo > limite) area?.rolarPor(fundo - limite);
      });
    });
  };
  useEffect(() => {
    if (!tec.alt || !edit) return;
    const t0 = setTimeout(seguirCursor, Platform.OS === 'ios' ? 350 : 120);
    return () => clearTimeout(t0);
  }, [tec.alt, tec.topo, !!edit, opcoes]); // eslint-disable-line react-hooks/exhaustive-deps

  // ─── desenho do conteúdo ───
  const estiloTexto = (x: QuadroTexto) => ({
    // G3: a altura da linha é sempre 1, 2 ou 3 linhas da folha, então as letras assentam nas linhas
    color: x.cor ? tinta(x.cor) : tinta('#111827'), fontSize: Math.max(6, medidasTexto(x).f * s),
    lineHeight: Math.max(8, medidasTexto(x).l * s), fontWeight: x.b ? ('800' as const) : ('400' as const),
    fontStyle: x.i ? ('italic' as const) : ('normal' as const), textDecorationLine: x.u ? ('underline' as const) : ('none' as const),
  });
  /** B5/B10: pedaços do texto com o estilo próprio de cada trecho (tamanho, N, I, S e cor). */
  const filhosTexto = (x: QuadroTexto) => spansDoTexto(x, s, tinta);

  // B7: largura total do que existe na folha (a barrinha de baixo deixa chegar a qualquer texto)
  const largTotal = Math.max(valor.larg, ...valor.textos.map(x => x.x + x.w + 20), ...(valor.tabelas ?? []).map(x => x.x + x.w + 20), ...(valor.imagens ?? []).map(x => x.x + x.w + 20));
  const visivelW = s ? vw / s : 0;
  // B12: o fundo é desenhado só na área visível (presa à grade da folha, em pedaços de 400), em qualquer zoom
  const fx0 = Math.floor(vista.ox / 400) * 400, fy0 = Math.floor(vista.oy / 400) * 400;
  const fx1 = Math.ceil((vista.ox + (s ? vw / s : 0)) / 400) * 400, fy1 = Math.ceil((vista.oy + (s ? vh / s : 0)) / 400) * 400;
  const fundoVisivel = useMemo(() => (
    <FolhaFundo folha={folha} x={fx0} y={fy0} larg={Math.max(400, fx1 - fx0)} alt={Math.max(400, fy1 - fy0)} fundo={fundo} escura={escura} idBase="vp" />
  ), [folha, fx0, fy0, fx1, fy1, fundo, escura]);

  const corpo = (
    <View ref={caixaRef} onLayout={e => { setVw(e.nativeEvent.layout.width); setVh(e.nativeEvent.layout.height); }}
      style={{ height: telaCheia ? undefined : alturaEf, flex: telaCheia || !alturaEf ? 1 : undefined, overflow: 'hidden', backgroundColor: fundo,
        borderRadius: telaCheia ? 0 : 14, borderWidth: telaCheia ? 0 : 1, borderColor: C.line }}>
      {s > 0 && (
        <Svg pointerEvents="none" width={vw} height={vh} viewBox={`${vista.ox} ${vista.oy} ${vw / s} ${vh / s}`} style={{ position: 'absolute', left: 0, top: 0 }}>
          {fundoVisivel}
        </Svg>
      )}
      {s > 0 && (
        <View ref={conteudoRef} collapsable={false} pointerEvents="none" style={{ position: 'absolute', left: -vista.ox * s, top: -vista.oy * s, width: W, height: H }}>
          <Svg width={W} height={H} viewBox={`0 0 ${valor.larg} ${valor.alt}`}>
            {/* a folha inteira só entra na foto ao exportar como imagem (na tela, o fundo é o da área visível, acima) */}
            {exportando && <FolhaFundo folha={folha} larg={valor.larg} alt={valor.alt} fundo={fundo} escura={escura} idBase={String(Math.round(valor.larg))} />}
            {/* limites da folha: esquerda e em cima */}
            <Line x1={1.5} y1={0} x2={1.5} y2={valor.alt} stroke={alpha(cor, 0.55)} strokeWidth={3} />
            <Line x1={0} y1={1.5} x2={valor.larg} y2={1.5} stroke={alpha(cor, 0.55)} strokeWidth={3} />
            {[...(apagando ?? valor.tracos), ...(atual ? [atual] : [])].map((tr, i) => {
              const mv = sel?.tipo === 'traco' && sel.i === i;
              return (
                <G key={i} transform={mv ? `translate(${desloc[0]} ${desloc[1]})` : undefined}>
                  <Path d={tr.d} stroke={tinta(tr.cor)} strokeWidth={tr.w} fill="none" strokeLinecap="round" strokeLinejoin="round" />
                </G>
              );
            })}
            {sel?.tipo === 'traco' && valor.tracos[sel.i] && (() => { const b = caixaTraco(valor.tracos[sel.i]); return (
              <Rect x={b.x + desloc[0]} y={b.y + desloc[1]} width={b.w} height={b.h} rx={8} fill="none" stroke={cor} strokeWidth={2} strokeDasharray="8 6" />
            ); })()}
            {mira && <Circle cx={mira[0]} cy={mira[1]} r={raio} fill={alpha('#FFFFFF', 0.12)} stroke={cor} strokeWidth={2} />}
          </Svg>
          {(valor.imagens ?? []).map(im => {
            const [dx, dy] = dsl('imagem', im.id);
            const src = fonteImagem(im.arq);
            return (
              <View key={im.id} style={[{ position: 'absolute', left: (im.x + dx) * s, top: (im.y + dy) * s, width: im.w * s, height: im.h * s, alignItems: 'center', justifyContent: 'center', backgroundColor: alpha(C.card2, 0.5) }, linhaSel('imagem', im.id)]}>
                {src && !quebrada[im.id] ? <Image source={{ uri: src }} style={{ width: '100%', height: '100%' }} resizeMode="contain" onError={() => setQuebrada(q => ({ ...q, [im.id]: true }))} />
                  : <Text style={{ color: C.sub, fontSize: 12, textAlign: 'center', padding: 8 }}>🖼️ {t('Imagem não encontrada')}</Text>}
              </View>
            );
          })}
          {(valor.tabelas ?? []).filter(tb => !(edit?.tipo === 'tabela' && edit.id === tb.id)).map(tb => {
            const [dx, dy] = dsl('tabela', tb.id);
            const cw = (tb.w * s) / Math.max(1, tb.linhas[0]?.length ?? 1);
            return (
              <View key={tb.id} onLayout={e => { alturas.current[tb.id] = e.nativeEvent.layout.height / (s || 1); }}
                style={[{ position: 'absolute', left: (tb.x + dx) * s, top: (tb.y + dy) * s, width: tb.w * s }, linhaSel('tabela', tb.id)]}>
                {tb.linhas.map((l, r) => (
                  <View key={r} style={{ flexDirection: 'row' }}>
                    {l.map((v, c) => <Text key={c} allowFontScaling={false} style={{ width: cw, minHeight: 36 * s, padding: 4, borderWidth: 1, borderColor: escura ? '#555' : '#C7CBD3', color: tinta('#111827'), fontSize: Math.max(6, (tb.tams?.[`${r}-${c}`] ?? TAM_CELULA) * s), fontWeight: r === 0 ? '700' : '400', backgroundColor: r === 0 ? alpha(cor, 0.12) : 'transparent' }}>{v}</Text>)}
                  </View>
                ))}
              </View>
            );
          })}
          {valor.checks.filter(c => !(edit?.tipo === 'check' && edit.id === c.id)).map(c => {
            const [dx, dy] = dsl('check', c.id);
            return (
              <View key={c.id} onLayout={e => { alturas.current[c.id] = e.nativeEvent.layout.height / (s || 1); }}
                style={[{ position: 'absolute', left: (c.x + dx) * s, top: (c.y + dy) * s, width: c.w * s, padding: 6 * s, gap: 2 }, linhaSel('check', c.id)]}>
                {c.itens.map(it => (
                  <View key={it.id} style={{ flexDirection: 'row', alignItems: 'center', gap: 8 * s }}>
                    <View style={{ width: 30 * s, height: 30 * s, borderRadius: 6 * s, borderWidth: 2, borderColor: it.done ? cor : tinta('#6B7280'), backgroundColor: it.done ? cor : 'transparent', alignItems: 'center', justifyContent: 'center' }}>
                      {it.done && <Text style={{ color: '#fff', fontSize: 18 * s, fontWeight: '900' }}>✓</Text>}
                    </View>
                    <Text style={{ flex: 1, color: tinta('#111827'), fontSize: Math.max(8, 36 * s), textDecorationLine: it.done ? 'line-through' : 'none', opacity: it.done ? 0.6 : 1 }}>{it.text || ' '}</Text>
                  </View>
                ))}
              </View>
            );
          })}
          {valor.textos.filter(x => !(edit?.tipo === 'texto' && edit.id === x.id)).map(x => {
            const [dx, dy] = dsl('texto', x.id);
            const vazio = !x.texto.trim();
            return (
              <View key={x.id} onLayout={e => { alturas.current[x.id] = e.nativeEvent.layout.height / (s || 1); }}
                style={[{ position: 'absolute', left: (x.x + dx) * s, top: (yT(x) + dy) * s, width: x.w * s, minHeight: 30 * s, paddingHorizontal: 4 }, linhaSel('texto', x.id)]}>
                <Text allowFontScaling={false} onTextLayout={e => { linhasTxt.current[x.id] = e.nativeEvent.lines; }}
                  style={[estiloTexto(x), vazio ? { color: alpha(tinta('#6B7280'), 0.8) } : null]}>{vazio ? (x.titulo ? t('Título') : '') : filhosTexto(x)}</Text>
              </View>
            );
          })}
        </View>
      )}
      {/* G5: régua por cima da folha (fora do conteúdo exportado) */}
      {s > 0 && regua.on && (
        <View pointerEvents="none" style={{ position: 'absolute', left: -vista.ox * s, top: -vista.oy * s, width: W, height: H }}>
          <Svg width={W} height={H} viewBox={`0 0 ${valor.larg} ${valor.alt}`}>{desenhoRegua}</Svg>
          <View style={{ position: 'absolute', left: regua.x * s - 34, top: regua.y * s - 16, width: 68, height: 32, borderRadius: 16, alignItems: 'center', justifyContent: 'center',
            backgroundColor: girando ? cor : alpha(C.card, 0.9), borderWidth: 1, borderColor: cor }}>
            <Text allowFontScaling={false} style={{ color: girando ? '#fff' : C.text, fontSize: 15, fontWeight: '800' }}>{grausRegua(regua.a)}°</Text>
          </View>
        </View>
      )}
      {/* camada que recebe os toques (por cima do conteúdo, por baixo do que está sendo editado) */}
      {s > 0 && <View {...pan.panHandlers} style={{ position: 'absolute', left: 0, top: 0, right: 0, bottom: 0 }} />}
      {/* o que está sendo editado fica por cima, com teclado */}
      {s > 0 && edit && (() => {
        if (edit.tipo === 'texto') { const x0 = valor.textos.find(y => y.id === edit.id); const x = x0 ? { ...x0, y: yT(x0) } : null; return x ? <EditaTexto key={x.id} x={x} s={s} px={px} py={py} estilo={estiloTexto(x)} cor={cor}
          filhos={filhosTexto(x)} selForcada={selForcada} onSel={setSelTxt} noRef={editNo} onCresce={seguirCursor}
          onChange={tx => {
            // B5: os trechos acompanham o que é digitado ou apagado; o estilo escolhido sem seleção vale para o que for digitado
            const q = ref.current.valor, y0 = q.textos.find(y => y.id === x.id);
            const trechos = y0 ? ajustarTrechos(y0.texto, tx, y0.trechos, pendente.current) : undefined;
            mudar(crescer({ ...q, textos: q.textos.map(y => (y.id === x.id ? { ...y, texto: tx, trechos } : y)) }, x.x + x.w, x.y + 200), false);
          }}
          onFim={() => { setEdit(null); const atualX = ref.current.valor.textos.find(y => y.id === x.id); if (atualX && !atualX.texto.trim() && !atualX.titulo) mudar({ ...ref.current.valor, textos: ref.current.valor.textos.filter(y => y.id !== x.id) }, false); }} /> : null; }
        if (edit.tipo === 'check') { const c = valor.checks.find(y => y.id === edit.id); return c ? <EditaCheck key={c.id} c={c} s={s} px={px} py={py} cor={cor} tinta={tinta}
          onChange={nv => mudar({ ...ref.current.valor, checks: ref.current.valor.checks.map(y => (y.id === c.id ? nv : y)) }, false)} onFim={() => setEdit(null)} /> : null; }
        const tb = (valor.tabelas ?? []).find(y => y.id === edit.id);
        return tb ? <EditaTabela key={tb.id} tb={tb} s={s} px={px} py={py} cor={cor} tinta={tinta} celSel={celSel} onCelSel={setCelSel}
          onChange={nv => mudar({ ...ref.current.valor, tabelas: (ref.current.valor.tabelas ?? []).map(y => (y.id === tb.id ? nv : y)) }, false)} onFim={() => setEdit(null)} /> : null;
      })()}
      {alca && (
        <View pointerEvents="none" style={{ position: 'absolute', left: alca.x - 26, top: alca.y - 26, width: 52, height: 52, borderRadius: 26, backgroundColor: alpha(cor, 0.9), alignItems: 'center', justifyContent: 'center' }}>
          <Text style={{ color: '#fff', fontSize: 26 }}>✥</Text>
        </View>
      )}
      {/* B7: barrinha de rolar de lado (aparece quando há coisa fora da tela, à direita ou à esquerda) */}
      {s > 0 && !sel && (largTotal > visivelW + 4 || vista.ox > 1) && (
        <RolagemLado larg={vw} total={largTotal} visivel={visivelW} ox={vista.ox} cor={cor} baixo={telaCheia ? ins.bottom : 0} sumir={telaCheia} sinal={`${vista.ox}:${vista.oy}:${vista.z}`} reserva={telaCheia && emCima ? (onTelaCheia ? 110 : 0) + (verTopo ? 96 : 0) : 0}
          onOx={ox => { const r = ref.current; r.vista = { ...r.vista, ox }; setVista(r.vista); }} />
      )}
      {sel && !telaCheia && <View style={{ position: 'absolute', bottom: 8, left: 8, right: 8 }}>{selAcoes()}</View>}
      {(vista.z !== 1) && (
        <Pressable onPress={() => setVista(v => ({ ...v, z: 1 }))} style={{ position: 'absolute', right: 8, top: telaCheia ? (emCima && !recolhida ? (livre?.y ?? 8) + tamBarraCheia.current.h + 8 : 62) : 8, paddingHorizontal: 8, paddingVertical: 4, borderRadius: 10, backgroundColor: alpha(C.card, 0.9) }}>
          <Text style={{ color: C.text, fontSize: 11 }}>{Math.round(vista.z * 100)}% · 100%</Text>
        </Pressable>
      )}
      {telaCheia && barraCheia}
      {sairBtn}
    </View>
  );

  const grupoMenu = (titulo: string, itens: [string, () => void, boolean?][], alvo?: (no: View | null) => void) => (
    <View ref={alvo} collapsable={false} style={{ gap: 4 }}>
      <Text style={{ color: C.sub, fontSize: 12, fontWeight: '800', letterSpacing: 0.6 }}>{titulo.toUpperCase()}</Text>
      <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 6 }}>
        {itens.map(([l, f, on]) => <Chip key={l} cor={cor} on={!!on} label={l} onPress={() => { setMenu(false); setTimeout(f, 200); }} />)}
      </View>
    </View>
  );

  return (
    <View style={{ gap: 8, flex: alturaEf && !telaCheia ? undefined : 1 }}>
      {!telaCheia && (
        <View ref={barraNo} collapsable={false} onLayout={e => { if (!fixaRef.current) setAltBarra(e.nativeEvent.layout.height); }}>
          {fixa && fixaCtx ? <View style={{ height: altBarra }} /> : compacto && !fixaCtx ? barraTeclado : barraNormal}
        </View>
      )}
      {corpo}

      {/* ⋮: Inserir, Ferramentas, Folha e Página */}
      <Modal visible={menu} transparent animationType="fade" onRequestClose={() => setMenu(false)}>
        <Pressable onPress={() => setMenu(false)} style={{ flex: 1, backgroundColor: C.overlay, justifyContent: 'flex-end' }}>
          <Pressable style={{ backgroundColor: C.card, borderTopLeftRadius: 22, borderTopRightRadius: 22, padding: 16, paddingBottom: 30, gap: 14 }}>
            {grupoMenu(t('Inserir'), [['☑️ ' + t('Checklist'), inserirCheck], ['▦ ' + t('Tabela'), () => setNovaTabela(true)], ['⬠ ' + t('Formas'), () => setFormas(true)], ['🖼️ ' + t('Imagem'), inserirImagem]])}
            {grupoMenu(t('Ferramentas'), [['🔲 ' + t('Selecionar'), () => setModo('selecionar')], ['✋ ' + t('Mover'), () => setModo('mover')], ['📏 ' + t('Régua') + (regua.on ? ' ✓' : ''), alternarRegua, regua.on], ['🧹 ' + t('Limpar'), limpar]])}
            {grupoMenu(t('Folha'), [['📄 ' + t('Tipo, cor e tema da folha'), () => setFolhaAberta(true)]], seGuia(alvoFolha))}
            {grupoMenu(t('Página'), [['⤒ ' + t('Voltar ao início da folha'), () => setVista({ ox: 0, oy: 0, z: 1 })], ['📕 ' + t('Exportar em PDF'), () => exportar('pdf')], ['🖼️ ' + t('Exportar como imagem'), () => exportar('imagem')]])}
          </Pressable>
        </Pressable>
      </Modal>

      <FolhaSheet visivel={folhaAberta} folha={folhaBase} cor={cor} rotuloTodas={onAplicarTodas ? rotuloTodas ?? t('Aplicar a todas as páginas desta pasta') : undefined}
        onMudar={f => mudar({ ...valor, folha: f })} onFechar={todas => { setFolhaAberta(false); if (todas) onAplicarTodas?.(ref.current.valor.folha ?? folhaBase); }} />

      <PaletaCores visivel={!!paleta} titulo={paleta?.sel ? t('Cor do item selecionado') : undefined} atual={paleta ? (paleta.sel ? corDoSel() : paleta.marca ? coresMarca[paleta.i] : config.bolinhas[paleta.i]) : ''} onFechar={() => setPaleta(null)}
        onEscolher={c => {
          if (!paleta) return;
          if (paleta.sel) { pintarSel(c); return; }
          if (paleta.marca) { setCorMarca(paleta.i, c); setBolM(paleta.i); return; }
          salvarBolinha(aba, paleta.i, c); if (modo === 'texto') { setBolTxt(paleta.i); mudarEstilo('cor', tinta(c)); } else setBol(paleta.i);
        }} />

      <Modal visible={formas} transparent animationType="fade" onRequestClose={() => setFormas(false)}>
        <Pressable onPress={() => setFormas(false)} style={{ flex: 1, backgroundColor: C.overlay, justifyContent: 'center', padding: 24 }}>
          <Pressable style={{ backgroundColor: C.card, borderRadius: 18, padding: 16, gap: 10 }}>
            <Text style={{ color: C.text, fontWeight: '800', fontSize: 16 }}>{t('Formas')}</Text>
            <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 8 }}>
              {([['linha', '╱ ' + t('Linha reta')], ['seta', '➝ ' + t('Seta')], ['retangulo', '▭ ' + t('Retângulo')], ['circulo', '◯ ' + t('Círculo')], ['triangulo', '△ ' + t('Triângulo')], ['estrela', '☆ ' + t('Estrela')]] as [Forma, string][])
                .map(([f, l]) => <Chip key={f} cor={cor} on={false} label={l} onPress={() => { setFormas(false); inserirForma(f); }} />)}
            </View>
            <Text style={{ color: C.sub, fontSize: 12 }}>{t('Dica: desenhe a forma à mão e segure o dedo no fim do traço para ela ficar perfeita.')}</Text>
          </Pressable>
        </Pressable>
      </Modal>

      {anexo.tela}
      <NovaTabela visivel={novaTabela} cor={cor} onFechar={() => setNovaTabela(false)} onCriar={(l, c) => { setNovaTabela(false); inserirTabela(l, c); }} />
    </View>
  );
}

/** Texto sendo escrito (fica por cima da camada de toques, com teclado).
 *  0.72 (texto 11b B5 / 11c B10): o texto vai como pedaços com estilo (filhos do campo), o campo conta a seleção para a barra
 *  e, nos dois toques, já abre com a palavra selecionada (as alças são as do próprio sistema, no iPhone e no Android). */
function EditaTexto({ x, s, px, py, estilo, cor, filhos, selForcada, onSel, onChange, onFim, noRef, onCresce }: { x: QuadroTexto; s: number; px: (v: number) => number; py: (v: number) => number; estilo: object; cor: string;
  filhos: ReactNode; selForcada: { start: number; end: number; n: number } | null; onSel: (sel: { start: number; end: number }) => void; onChange: (t: string) => void; onFim: () => void;
  noRef?: { current: View | null }; onCresce?: () => void }) {
  const campo = useRef<TextInput | null>(null);
  const [forcada, setForcada] = useState<{ start: number; end: number } | undefined>(undefined);
  // seleção pedida pela barra (depois de mudar o estilo do trecho) ou pelos dois toques: aplica uma vez e solta
  useEffect(() => {
    if (!selForcada) return;
    setForcada({ start: selForcada.start, end: selForcada.end });
    const t0 = setTimeout(() => { campo.current?.focus(); setForcada({ start: selForcada.start, end: selForcada.end }); }, 60);
    const t1 = setTimeout(() => setForcada(undefined), 260);
    return () => { clearTimeout(t0); clearTimeout(t1); };
  }, [selForcada?.n]); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    // a borda de 2 px fica por fora, para as letras continuarem no mesmo lugar (assentadas na linha)
    <View ref={no => { if (noRef) noRef.current = no; }} collapsable={false} style={{ position: 'absolute', left: px(x.x) - 2, top: py(x.y) - 2, width: x.w * s + 4, minHeight: 30 * s, borderWidth: 2, borderColor: cor, borderStyle: 'dashed', borderRadius: 8 }}>
      <TextInput ref={campo} allowFontScaling={false} multiline autoFocus onChangeText={onChange} onBlur={onFim} onContentSizeChange={() => onCresce?.()} placeholder={x.titulo ? t('Título') : t('Escreva aqui')} placeholderTextColor={C.mute}
        selection={forcada} onSelectionChange={e => onSel(e.nativeEvent.selection)} scrollEnabled={false}
        style={[estilo, { paddingHorizontal: 4, paddingVertical: 0, textAlignVertical: 'top' }]}>
        {x.texto ? filhos : null}
      </TextInput>
    </View>
  );
}

/** Checklist sendo editada: Enter cria item logo abaixo; apagar num item vazio tira o item. */
function EditaCheck({ c, s, px, py, cor, tinta, onChange, onFim }: { c: QuadroCheck; s: number; px: (v: number) => number; py: (v: number) => number; cor: string; tinta: (c: string) => string; onChange: (c: QuadroCheck) => void; onFim: () => void }) {
  const refs = useRef<Record<string, TextInput | null>>({});
  const [foco, setFoco] = useState<string | null>(c.itens[c.itens.length - 1]?.id ?? null);
  return (
    <View style={{ position: 'absolute', left: px(c.x), top: py(c.y), width: Math.max(220, c.w * s), padding: 6, gap: 2, borderWidth: 2, borderColor: cor, borderStyle: 'dashed', borderRadius: 8, backgroundColor: alpha(C.card, 0.92) }}>
      {c.itens.map((it, i) => (
        <View key={it.id} style={{ flexDirection: 'row', alignItems: 'center', gap: 6 }}>
          <Pressable hitSlop={6} onPress={() => onChange({ ...c, itens: c.itens.map(y => (y.id === it.id ? { ...y, done: !y.done } : y)) })}
            style={{ width: 20, height: 20, borderRadius: 5, borderWidth: 2, borderColor: it.done ? cor : C.mute, backgroundColor: it.done ? cor : 'transparent', alignItems: 'center', justifyContent: 'center' }}>
            {it.done && <Text style={{ color: '#fff', fontSize: 11, fontWeight: '900' }}>✓</Text>}
          </Pressable>
          <TextInput ref={r => { refs.current[it.id] = r; }} value={it.text} autoFocus={foco === it.id} placeholder={t('Item')} placeholderTextColor={C.mute} blurOnSubmit={false} returnKeyType="next"
            style={{ flex: 1, minWidth: 0, color: C.text, fontSize: Math.max(13, 36 * s), paddingVertical: 3 }}
            onChangeText={v => onChange({ ...c, itens: c.itens.map(y => (y.id === it.id ? { ...y, text: v } : y)) })}
            onSubmitEditing={() => { const n: Item = { id: uid(), text: '', done: false }; const a = [...c.itens]; a.splice(i + 1, 0, n); onChange({ ...c, itens: a }); setFoco(n.id); setTimeout(() => refs.current[n.id]?.focus(), 80); }}
            onKeyPress={e => {
              if (e.nativeEvent.key === 'Backspace' && !it.text && c.itens.length > 1) {
                const ant = c.itens[i - 1]; onChange({ ...c, itens: c.itens.filter(y => y.id !== it.id) });
                if (ant) setTimeout(() => refs.current[ant.id]?.focus(), 60);
              }
            }} />
        </View>
      ))}
      <Pressable onPress={() => { const vazios = c.itens.filter(i => !i.text.trim()); if (vazios.length && vazios.length < c.itens.length) onChange({ ...c, itens: c.itens.filter(i => i.text.trim()) }); onFim(); }} style={{ alignSelf: 'flex-end', paddingHorizontal: 10, paddingVertical: 4 }}>
        <Text style={{ color: cor, fontWeight: '800' }}>{t('Pronto')}</Text>
      </Pressable>
    </View>
  );
}

/** Tabela sendo editada: digitar nas células, acrescentar ou tirar linhas e colunas.
 *  0.72 (texto 11b B5): a célula tocada fica escolhida (o tamanho da letra da barra vale para ela); "Escolher células"
 *  deixa marcar várias de uma vez. */
function EditaTabela({ tb, s, px, py, cor, celSel, onCelSel, onChange, onFim }: { tb: QuadroTabela; s: number; px: (v: number) => number; py: (v: number) => number; cor: string; tinta: (c: string) => string;
  celSel: string[]; onCelSel: (k: string[]) => void; onChange: (t: QuadroTabela) => void; onFim: () => void }) {
  const cols = tb.linhas[0]?.length ?? 0;
  const cw = Math.max(70, (tb.w * s) / Math.max(1, cols));
  const [varias, setVarias] = useState(false);
  const setCel = (r: number, c: number, v: string) => onChange({ ...tb, linhas: tb.linhas.map((l, i) => (i === r ? l.map((y, j) => (j === c ? v : y)) : l)) });
  const tamDe = (k: string) => Math.max(11, (tb.tams?.[k] ?? TAM_CELULA) * s);
  return (
    <View style={{ position: 'absolute', left: px(tb.x), top: py(tb.y), gap: 4, padding: 4, borderWidth: 2, borderColor: cor, borderStyle: 'dashed', borderRadius: 8, backgroundColor: alpha(C.card, 0.95) }}>
      <ScrollView horizontal nestedScrollEnabled keyboardShouldPersistTaps="handled">
        <View>
          {tb.linhas.map((l, r) => (
            <View key={r} style={{ flexDirection: 'row' }}>
              {l.map((v, c) => {
                const k = `${r}-${c}`, on = celSel.includes(k);
                const estiloCel = { width: cw, minHeight: 34, color: C.text, borderWidth: on ? 2 : 1, borderColor: on ? cor : C.line, padding: 4, fontSize: tamDe(k), fontWeight: r === 0 ? '700' as const : '400' as const,
                  backgroundColor: on ? alpha(cor, 0.16) : r === 0 ? alpha(cor, 0.12) : 'transparent' };
                return varias ? (
                  <Pressable key={c} onPress={() => onCelSel(on ? celSel.filter(y => y !== k) : [...celSel, k])} accessibilityRole="button" accessibilityState={{ selected: on }}>
                    <Text allowFontScaling={false} style={estiloCel}>{v || ' '}</Text>
                  </Pressable>
                ) : (
                  <TextInput key={c} value={v} onChangeText={x => setCel(r, c, x)} multiline autoFocus={r === 0 && c === 0 && !v} allowFontScaling={false}
                    onFocus={() => onCelSel([k])} style={estiloCel} />
                );
              })}
            </View>
          ))}
        </View>
      </ScrollView>
      <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 12, alignItems: 'center' }}>
        <Text style={{ color: cor, fontSize: 12 }} onPress={() => onChange({ ...tb, linhas: [...tb.linhas, Array(cols).fill('')] })}>+ {t('linha')}</Text>
        <Text style={{ color: cor, fontSize: 12 }} onPress={() => onChange({ ...tb, w: tb.w + tb.w / Math.max(1, cols), linhas: tb.linhas.map(l => [...l, '']) })}>+ {t('coluna')}</Text>
        {tb.linhas.length > 1 && <Text style={{ color: C.sub, fontSize: 12 }} onPress={() => onChange({ ...tb, linhas: tb.linhas.slice(0, -1) })}>− {t('linha')}</Text>}
        {cols > 1 && <Text style={{ color: C.sub, fontSize: 12 }} onPress={() => onChange({ ...tb, w: tb.w - tb.w / cols, linhas: tb.linhas.map(l => l.slice(0, -1)) })}>− {t('coluna')}</Text>}
        <Text style={{ color: varias ? cor : C.sub, fontSize: 12, fontWeight: varias ? '800' : '400' }} onPress={() => setVarias(v => !v)}>{(varias ? '☑ ' : '☐ ') + t('Escolher células')}</Text>
        <Text style={{ color: cor, fontWeight: '800', marginLeft: 'auto' }} onPress={onFim}>{t('Pronto')}</Text>
      </View>
    </View>
  );
}

/** Escolher quantas linhas e colunas a tabela nova tem. */
function NovaTabela({ visivel, cor, onFechar, onCriar }: { visivel: boolean; cor: string; onFechar: () => void; onCriar: (l: number, c: number) => void }) {
  const [l, setL] = useState(3); const [c, setC] = useState(3);
  const passo = (v: number, set: (n: number) => void, rot: string) => (
    <View style={{ flexDirection: 'row', alignItems: 'center', gap: 10 }}>
      <Text style={{ color: C.text, width: 90 }}>{rot}</Text>
      <Pressable onPress={() => set(Math.max(1, v - 1))} style={{ width: 36, height: 36, borderRadius: 18, backgroundColor: C.card2, alignItems: 'center', justifyContent: 'center' }}><Text style={{ color: C.text, fontSize: 18 }}>−</Text></Pressable>
      <Text style={{ color: C.text, fontWeight: '800', fontSize: 16, width: 30, textAlign: 'center' }}>{v}</Text>
      <Pressable onPress={() => set(Math.min(20, v + 1))} style={{ width: 36, height: 36, borderRadius: 18, backgroundColor: C.card2, alignItems: 'center', justifyContent: 'center' }}><Text style={{ color: C.text, fontSize: 18 }}>+</Text></Pressable>
    </View>
  );
  return (
    <Modal visible={visivel} transparent animationType="fade" onRequestClose={onFechar}>
      <Pressable onPress={onFechar} style={{ flex: 1, backgroundColor: C.overlay, justifyContent: 'center', padding: 24 }}>
        <Pressable style={{ backgroundColor: C.card, borderRadius: 18, padding: 16, gap: 12 }}>
          <Text style={{ color: C.text, fontWeight: '800', fontSize: 16 }}>▦ {t('Tabela')}</Text>
          {passo(l, setL, t('Linhas'))}
          {passo(c, setC, t('Colunas'))}
          <Pressable onPress={() => onCriar(l, c)} style={{ backgroundColor: cor, borderRadius: 12, padding: 12 }}><Text style={{ color: '#fff', textAlign: 'center', fontWeight: '800' }}>{t('Inserir')}</Text></Pressable>
        </Pressable>
      </Pressable>
    </Modal>
  );
}

/** ⋮ › Folha: tipo, cor e tema (com a força do desenho) e "Aplicar a todas as páginas desta matéria/pasta". */
function FolhaSheet({ visivel, folha, cor, rotuloTodas, onMudar, onFechar }: { visivel: boolean; folha: Folha; cor: string; rotuloTodas?: string; onMudar: (f: Folha) => void; onFechar: (todas: boolean) => void }) {
  const [todas, setTodas] = useState(false);
  const mud = (p: Partial<Folha>) => onMudar({ ...folha, ...p });
  return (
    <Modal visible={visivel} transparent animationType="fade" onRequestClose={() => onFechar(false)}>
      <Pressable onPress={() => onFechar(false)} style={{ flex: 1, backgroundColor: C.overlay, justifyContent: 'flex-end' }}>
        <Pressable style={{ backgroundColor: C.card, borderTopLeftRadius: 22, borderTopRightRadius: 22, padding: 16, paddingBottom: 30, gap: 12, maxHeight: '85%' }}>
          <ScrollView contentContainerStyle={{ gap: 12 }}>
            <FolhaOpcoes folha={folha} cor={cor} onMudar={mud} />
            {!!rotuloTodas && (
              <Pressable onPress={() => setTodas(x => !x)} style={{ flexDirection: 'row', alignItems: 'center', gap: 10 }}>
                <Text style={{ fontSize: 20, color: C.text }}>{todas ? '☑' : '☐'}</Text>
                <Text style={{ color: C.text, flex: 1 }}>{rotuloTodas}</Text>
              </Pressable>
            )}
            <Pressable onPress={() => { onFechar(todas); setTodas(false); }} style={{ backgroundColor: cor, borderRadius: 12, padding: 12 }}><Text style={{ color: '#fff', textAlign: 'center', fontWeight: '800' }}>{t('Pronto')}</Text></Pressable>
          </ScrollView>
        </Pressable>
      </Pressable>
    </Modal>
  );
}

/** Opções da folha (também usadas em Ajustes › Páginas e em Ajustes › Abas › Páginas desta aba). */
export function FolhaOpcoes({ folha, cor, onMudar }: { folha: Folha; cor: string; onMudar: (p: Partial<Folha>) => void }) {
  const escuroApp = modoDoApp() === 'escuro';
  const [corDesenho, setCorDesenho] = useState(false);   // 0.67.2: "Cor do desenho" (Automática ou uma cor)
  return (
    <View style={{ gap: 12 }}>
      <Text style={{ color: C.sub, fontSize: 12, fontWeight: '800' }}>{t('TIPO DE FOLHA')}</Text>
      <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 6 }}>
        {TIPOS_FOLHA.map(([k, n]) => <Chip key={k} small cor={cor} on={folha.tipo === k} label={t(n)} onPress={() => onMudar({ tipo: k })} />)}
      </View>
      <Text style={{ color: C.sub, fontSize: 12, fontWeight: '800' }}>{t('COR DA FOLHA')}</Text>
      <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 10 }}>
        {CORES_FOLHA.map(([c, n]) => (
          <Pressable key={c} onPress={() => onMudar({ cor: c })} accessibilityLabel={t(n)} style={{ alignItems: 'center', gap: 2, width: 58 }}>
            <View style={{ width: 34, height: 34, borderRadius: 17, backgroundColor: fundoFolha({ ...folha, cor: c }, escuroApp), borderWidth: folha.cor === c ? 3 : 1, borderColor: folha.cor === c ? cor : C.line }} />
            <Text style={{ color: C.sub, fontSize: 10, textAlign: 'center' }} numberOfLines={2}>{t(n)}</Text>
          </Pressable>
        ))}
      </View>
      <Text style={{ color: C.sub, fontSize: 12, fontWeight: '800' }}>{t('TEMA DECORATIVO')}</Text>
      <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 6 }}>
        <Chip small cor={cor} on={!folha.tema} label={t('Sem tema')} onPress={() => onMudar({ tema: null })} />
        {TEMAS_FOLHA.map(([k, n]) => <Chip key={k} small cor={cor} on={folha.tema === k} label={t(n)} onPress={() => onMudar({ tema: k })} />)}
      </View>
      {!!folha.tema && (
        <View style={{ gap: 6 }}>
          <Text style={{ color: C.sub, fontSize: 12 }}>{t('Desenho mais fraco ou mais forte')}</Text>
          <View style={{ flexDirection: 'row', gap: 6, alignItems: 'center' }}>
            <Text style={{ color: C.sub }}>−</Text>
            {[0.15, 0.35, 0.55, 0.75, 1].map(v => (
              <Pressable key={v} onPress={() => onMudar({ forca: v })} style={{ flex: 1, height: 22, borderRadius: 6, backgroundColor: folha.forca >= v - 0.01 ? cor : C.card2 }} />
            ))}
            <Text style={{ color: C.sub }}>+</Text>
          </View>
          <Pressable onPress={() => setCorDesenho(true)} style={{ flexDirection: 'row', alignItems: 'center', gap: 8, paddingVertical: 6 }}>
            <Text style={{ color: C.text, flex: 1 }}>{t('Cor do desenho')}</Text>
            {folha.corTema
              ? <View style={{ width: 20, height: 20, borderRadius: 10, backgroundColor: folha.corTema, borderWidth: 1, borderColor: C.line }} />
              : <Text style={{ color: C.sub }}>{t('Automática')}</Text>}
            <Text style={{ color: C.mute, fontSize: 18 }}>›</Text>
          </Pressable>
        </View>
      )}
      <PaletaCores visivel={corDesenho} atual={folha.corTema ?? ''} titulo={t('Cor do desenho')} onFechar={() => setCorDesenho(false)} onEscolher={c => onMudar({ corTema: c })}
        automatica={{ on: !folha.corTema, desc: t('As cores próprias de cada tema: floral rosado, folhas verdes, céu azul...'), onEscolher: () => onMudar({ corTema: null }) }} />
      <View style={{ height: 70, borderRadius: 12, overflow: 'hidden', borderWidth: 1, borderColor: C.line }}>
        <Svg width="100%" height={70} viewBox="0 0 1000 200" preserveAspectRatio="xMinYMin slice">
          <FolhaFundo folha={folha} larg={1000} alt={200} fundo={fundoFolha(folha, escuroApp)} escura={folhaEscura(folha, escuroApp)} idBase="previa" />
        </Svg>
      </View>
    </View>
  );
}

// ─── Peças da barra (0.72, texto 11b) ─────────────────────────────────────────────────────────
/** Pedaços do texto com o estilo próprio de cada trecho (usado no quadro, no editor e no desenho salvo). */
export function spansDoTexto(x: QuadroTexto, s: number, tinta: (c: string) => string): ReactNode {
  const segs = segmentos(x);
  if (segs.length === 1 && !Object.keys(segs[0].e).length) return x.texto;
  return segs.map(g => {
    const e = g.e;
    if (!Object.keys(e).length) return <Text key={g.a}>{g.texto}</Text>;
    return (
      <Text key={g.a} style={{
        ...(e.tam != null ? { fontSize: Math.max(5, e.tam * s) } : null),
        ...(e.b != null ? { fontWeight: e.b ? ('800' as const) : ('400' as const) } : null),
        ...(e.i != null ? { fontStyle: e.i ? ('italic' as const) : ('normal' as const) } : null),
        ...(e.u != null ? { textDecorationLine: e.u ? ('underline' as const) : ('none' as const) } : null),
        ...(e.cor ? { color: tinta(e.cor) } : null),
      }}>{g.texto}</Text>
    );
  });
}

export type NomeIcone = 'texto' | 'caneta' | 'marca' | 'borracha' | 'esconder' | 'regua' | 'voltar' | 'refazer' | 'telaCheia' | 'mais';
/** Ícones de traço da barra (desenhados aqui, sem pacote novo). */
function Icone({ nome, cor, tam = 22 }: { nome: NomeIcone; cor: string; tam?: number }) {
  const p = { stroke: cor, strokeWidth: 2, fill: 'none', strokeLinecap: 'round' as const, strokeLinejoin: 'round' as const };
  return (
    <Svg width={tam} height={tam} viewBox="0 0 24 24">
      {nome === 'texto' && <Path d="M5 5h14M12 5v14M9 19h6" {...p} strokeWidth={2.4} />}
      {nome === 'caneta' && <Path d="M4 20l1-4L16 5l3 3L8 19l-4 1zM14 7l3 3" {...p} />}
      {nome === 'marca' && <><Path d="M6 15l9-9 3 3-9 9H6v-3zM13 8l3 3" {...p} /><Path d="M4 21h9" {...p} strokeWidth={3} /></>}
      {nome === 'borracha' && <><Path d="M8 17l-4-4 8-8 7 7-5 5H8zM9 10l6 6" {...p} /><Path d="M8 21h12" {...p} /></>}
      {nome === 'esconder' && <Path d="M6 15l6-6 6 6" {...p} strokeWidth={2.4} />}
      {nome === 'regua' && <Path d="M3 9h18v6H3zM7 9v3M11 9v3M15 9v3M19 9v3" {...p} />}
      {nome === 'voltar' && <Path d="M9 14l-5-5 5-5M4 9h10a6 6 0 010 12h-3" {...p} />}
      {nome === 'refazer' && <Path d="M15 14l5-5-5-5M20 9H10a6 6 0 000 12h3" {...p} />}
      {nome === 'telaCheia' && <Path d="M4 9V4h5M15 4h5v5M20 15v5h-5M9 20H4v-5" {...p} />}
      {nome === 'mais' && <><Circle cx={6} cy={12} r={1.8} fill={cor} /><Circle cx={12} cy={12} r={1.8} fill={cor} /><Circle cx={18} cy={12} r={1.8} fill={cor} /></>}
    </Svg>
  );
}

/** Botão da barra: ícone em cima e nome embaixo, no mínimo 44 pt (B1). A ferramenta ativa fica destacada. */
function BotaoBarra({ icone, rotulo, on, desab, cor, onPress, alvo, min = 54, seta, rotuloAcessivel }: { icone: NomeIcone; rotulo: string; on?: boolean; desab?: boolean; cor: string;
  onPress: () => void; alvo?: (no: View | null) => void; min?: number; seta?: string; rotuloAcessivel?: string }) {
  const corIc = on ? cor : C.text;
  return (
    <Pressable ref={alvo} collapsable={false} onPress={onPress} disabled={desab} accessibilityRole="button" accessibilityLabel={rotuloAcessivel ?? rotulo}
      accessibilityState={{ selected: !!on, disabled: !!desab }}
      style={({ pressed }) => ({ flex: 1, minWidth: min, minHeight: 54, paddingVertical: 5, paddingHorizontal: 2, borderRadius: 12, borderWidth: 1, alignItems: 'center', justifyContent: 'center', gap: 2,
        borderColor: on ? cor : C.line, backgroundColor: on ? alpha(cor, 0.18) : pressed ? alpha(cor, 0.12) : C.card2, opacity: desab ? 0.4 : 1 })}>
      <Icone nome={icone} cor={corIc} />
      <Text numberOfLines={1} adjustsFontSizeToFit minimumFontScale={0.7} style={{ color: corIc, fontSize: 10.5, letterSpacing: -0.2, fontWeight: on ? '800' : '500' }}>{rotulo}{seta ? ' ' + seta : ''}</Text>
    </Pressable>
  );
}

/** Barrinha de ajuste (B4/B5): puxar ou tocar; anda junto com as 4 bolinhas. */
function Deslizante({ valor, min, max, cor, onMudar, rotulo }: { valor: number; min: number; max: number; cor: string; onMudar: (v: number) => void; rotulo: string }) {
  const larg = useRef(0);
  const [largS, setLargS] = useState(0);
  const vivo = useRef({ onMudar, min, max, ultimo: valor }); vivo.current = { ...vivo.current, onMudar, min, max };
  const [arrasto, setArrasto] = useState<number | null>(null);
  const de = (x: number) => {
    const r = vivo.current, w = larg.current - 24;
    const v = Math.round(r.min + Math.max(0, Math.min(1, (x - 12) / Math.max(1, w))) * (r.max - r.min));
    setArrasto(v);
    if (v !== r.ultimo) { r.ultimo = v; r.onMudar(v); }
  };
  const pan = useMemo(() => PanResponder.create({
    onStartShouldSetPanResponder: () => true,
    onMoveShouldSetPanResponder: () => true,
    onPanResponderTerminationRequest: () => false,
    onPanResponderGrant: e => { vivo.current.ultimo = NaN; de(e.nativeEvent.locationX); },
    onPanResponderMove: e => de(e.nativeEvent.locationX),
    onPanResponderRelease: () => setArrasto(null),
    onPanResponderTerminate: () => setArrasto(null),
  }), []); // eslint-disable-line react-hooks/exhaustive-deps
  const v = arrasto ?? valor, k = (Math.max(min, Math.min(max, v)) - min) / Math.max(1, max - min);
  return (
    <View {...pan.panHandlers} onLayout={e => { larg.current = e.nativeEvent.layout.width; setLargS(e.nativeEvent.layout.width); }} accessible accessibilityRole="adjustable" accessibilityLabel={rotulo}
      accessibilityValue={{ min, max, now: v }}
      onAccessibilityAction={e => { const n = e.nativeEvent.actionName === 'increment' ? v + 1 : v - 1; onMudar(Math.max(min, Math.min(max, n))); }}
      accessibilityActions={[{ name: 'increment' }, { name: 'decrement' }]}
      style={{ height: 44, justifyContent: 'center' }}>
      <View pointerEvents="none" style={{ marginHorizontal: 12, height: 6, borderRadius: 3, backgroundColor: C.line }}>
        <View style={{ width: `${k * 100}%`, height: 6, borderRadius: 3, backgroundColor: cor }} />
      </View>
      <View pointerEvents="none" style={{ position: 'absolute', left: 12 + k * Math.max(0, largS - 24) - 12, width: 24, height: 24, borderRadius: 12, backgroundColor: cor,
        borderWidth: 3, borderColor: C.card, shadowColor: '#000', shadowOpacity: 0.2, shadowRadius: 3, shadowOffset: { width: 0, height: 1 }, elevation: 3 }} />
    </View>
  );
}

/** B7: barrinha de rolar de lado no pé do quadro (puxar o "polegar" ou tocar na trilha). */
function RolagemLado({ larg, total, visivel, ox, cor, baixo, reserva = 0, onOx, sumir, sinal }: { larg: number; total: number; visivel: number; ox: number; cor: string; baixo: number; reserva?: number; onOx: (ox: number) => void;
  /** texto 28 (C1): na tela cheia a barrinha aparece só enquanto a folha mexe e some sozinha depois de ~1,5 s */
  sumir?: boolean; sinal?: string }) {
  const op = useRef(new Animated.Value(sumir ? 0 : 1)).current;
  const [vis, setVis] = useState(!sumir);
  const segurando = useRef(false);
  const tempo = useRef<ReturnType<typeof setTimeout> | null>(null);
  const esconderDepois = () => {
    if (tempo.current) clearTimeout(tempo.current);
    tempo.current = setTimeout(() => {
      if (segurando.current) return;
      Animated.timing(op, { toValue: 0, duration: 250, useNativeDriver: false }).start(({ finished }) => { if (finished) setVis(false); });
    }, 1500);
  };
  const mostrar = () => { if (!sumir) return; op.stopAnimation(); op.setValue(1); setVis(true); esconderDepois(); };
  const primeira = useRef(true);
  useEffect(() => { if (primeira.current) { primeira.current = false; return; } mostrar(); }, [sinal]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { if (!sumir) { op.setValue(1); setVis(true); } else { op.setValue(0); setVis(false); } }, [sumir]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => () => { if (tempo.current) clearTimeout(tempo.current); }, []);
  const trilha = Math.max(40, larg - 16 - reserva);
  const maxOx = Math.max(ox, total - visivel);
  const pol = Math.max(44, Math.min(trilha, (trilha * visivel) / Math.max(visivel, total)));
  const vivo = useRef({ trilha, pol, maxOx, onOx }); vivo.current = { trilha, pol, maxOx, onOx };
  const pan = useMemo(() => PanResponder.create({
    onStartShouldSetPanResponder: () => true,
    onMoveShouldSetPanResponder: () => true,
    onPanResponderTerminationRequest: () => false,
    onPanResponderGrant: e => { segurando.current = true; mover(e.nativeEvent.locationX); },
    onPanResponderMove: e => mover(e.nativeEvent.locationX),
    onPanResponderRelease: () => { segurando.current = false; esconderMaisTarde.current(); },
    onPanResponderTerminate: () => { segurando.current = false; esconderMaisTarde.current(); },
  }), []); // eslint-disable-line react-hooks/exhaustive-deps
  const esconderMaisTarde = useRef(() => {}); esconderMaisTarde.current = () => { if (sumir) esconderDepois(); };
  function mover(x: number) {
    const r = vivo.current;
    const k = Math.max(0, Math.min(1, (x - r.pol / 2) / Math.max(1, r.trilha - r.pol)));
    r.onOx(Math.round(k * r.maxOx));
  }
  const k = maxOx > 0 ? ox / maxOx : 0;
  if (!vis) return null;
  return (
    <Animated.View {...pan.panHandlers} accessible accessibilityRole="adjustable" accessibilityLabel={t('Rolar a folha para os lados')}
      style={{ position: 'absolute', left: 8, bottom: 2 + baixo, width: trilha, height: 26, justifyContent: 'center', opacity: op }}>
      <View pointerEvents="none" style={{ height: 6, borderRadius: 3, backgroundColor: alpha(C.sub, 0.25) }} />
      <View pointerEvents="none" style={{ position: 'absolute', left: k * (trilha - pol), width: pol, height: 8, borderRadius: 4, backgroundColor: alpha(cor, 0.8) }} />
    </Animated.View>
  );
}

// ─── Barra fixa no topo da página (0.72, texto 11b B2) ───────────────────────────────────────
type BarraFixa = {
  y: { current: number }; topo: { current: number | null };
  assinar: (f: () => void) => () => void;
  /** texto 18 A3: arrastar a página com o dedo (fecha a janelinha de opções) */
  assinarArraste: (f: () => void) => () => void;
  mostrar: (dono: string, no: ReactNode | null) => void;
  /** texto 18 A4: linha de ferramentas logo acima do teclado */
  mostrarRodape: (dono: string, no: ReactNode | null) => void;
  altRodape: { current: number };
  /** texto 20 (7): altura visível da página e onde ela começa na tela */
  altVisivel: { current: number }; yRaiz: { current: number | null };
  setRolagem: (on: boolean) => void;
  /** texto 18 A1: página aberta para escrever (Estudos > Página, páginas do Diário): a barra gruda ao rolar sem precisar
   *  tocar na folha antes. Nas telas de visão geral (Meu Dia, formulários) ela só gruda depois de tocar na folha. */
  editor: boolean;
};
const BarraFixaCtx = createContext<BarraFixa | null>(null);

/** ScrollView da página com o quadro: quando a barra do quadro sobe para fora da tela, ela fica presa logo abaixo do
 *  título da janela enquanto a página rola (e volta para o lugar dela ao rolar de volta). Use no lugar do ScrollView.
 *  Texto 18: só gruda com a folha em uso e enquanto a folha está na tela; com o teclado aberto, a barra vira uma linha
 *  só, logo acima do teclado (rodapé). */
export function PaginaRolavel({ children, onScroll, scrollEnabled, style, Rolagem = ScrollArrastavel, topoExtra = 0, editor = false, ...p }: ScrollViewProps & { children: ReactNode;
  /** a rolagem por dentro (ex.: a ScrollArrastavel, para as listas de arrastar continuarem rolando a página) */
  Rolagem?: ComponentType<any>;
  /** telas sem título fixo em cima (Screen): a barra fica presa abaixo da área da hora/bateria */
  topoExtra?: number;
  /** página aberta para escrever (ver BarraFixa.editor) */
  editor?: boolean }) {
  const raiz = useRef<View | null>(null);
  const largo = useTamanho() !== 'celular';   // texto 12 B: no navegador largo, o texto fica numa largura confortável de leitura
  const y = useRef(0), topo = useRef<number | null>(null);
  const ouvintes = useRef(new Set<() => void>());
  const arrastes = useRef(new Set<() => void>());
  const altRodape = useRef(0);
  const altVisivel = useRef(0), yRaiz = useRef<number | null>(null);
  const rolagemRef = useRef<ScrollView | null>(null);
  // texto 20 (2): "⬆ Topo" depois de rolar mais ou menos meia tela
  const [mostrarTopo, setMostrarTopo] = useState(false);
  const [presa, setPresa] = useState<{ dono: string; no: ReactNode } | null>(null);
  const [rodape, setRodape] = useState<{ dono: string; no: ReactNode } | null>(null);
  const [rolar, setRolar] = useState(true);
  const tec = useTeclado();
  const [baixo, setBaixo] = useState(0);
  const medirTopo = () => raiz.current?.measureInWindow((_x, yy, _w, h) => { topo.current = yy + topoExtra; yRaiz.current = yy; if (h) altVisivel.current = h; });
  // onde o teclado começa, medido a partir do pé desta página
  useEffect(() => {
    if (!tec.alt) { setBaixo(0); return; }
    raiz.current?.measureInWindow((_x, yy, _w, h) => setBaixo(Math.max(0, Math.round(yy + h - tec.topo))));
  }, [tec.alt, tec.topo]);
  const ctx = useMemo<BarraFixa>(() => ({
    y, topo, altRodape, editor, altVisivel, yRaiz,
    assinar: f => { ouvintes.current.add(f); return () => { ouvintes.current.delete(f); }; },
    assinarArraste: f => { arrastes.current.add(f); return () => { arrastes.current.delete(f); }; },
    mostrar: (dono, no) => setPresa(a => (no ? { dono, no } : a?.dono === dono ? null : a)),
    mostrarRodape: (dono, no) => setRodape(a => (no ? { dono, no } : a?.dono === dono ? null : a)),
    setRolagem: setRolar,
  }), [editor]);
  return (
    <BarraFixaCtx.Provider value={ctx}>
      <View ref={raiz} collapsable={false} style={[{ flex: 1 }, style as any]} onLayout={medirTopo}>
        <Rolagem {...ROLAGEM_TECLADO} {...p} rolagemRef={rolagemRef} style={{ flex: 1 }} scrollEnabled={rolar && scrollEnabled !== false} scrollEventThrottle={16}
          contentContainerStyle={largo ? [p.contentContainerStyle, { width: '100%', maxWidth: LARGURA_LEITURA, alignSelf: 'center' }] : p.contentContainerStyle}
          onScrollBeginDrag={(e: NativeSyntheticEvent<NativeScrollEvent>) => { medirTopo(); arrastes.current.forEach(f => f()); p.onScrollBeginDrag?.(e); }}
          onScroll={(e: NativeSyntheticEvent<NativeScrollEvent>) => {
            y.current = e.nativeEvent.contentOffset.y; ouvintes.current.forEach(f => f()); onScroll?.(e);
            const ver = y.current > (altVisivel.current || 600) / 2;
            setMostrarTopo(v => (v === ver ? v : ver));
          }}>
          {children}
          {/* espaço da linha de ferramentas de cima do teclado, para o fim da página não ficar escondido atrás dela */}
          {!!rodape && tec.alt > 0 && <View style={{ height: 64 }} />}
        </Rolagem>
        {!!presa && !(rodape && tec.alt > 0) && (
          <View style={{ position: 'absolute', top: 0, left: 0, right: 0, zIndex: 30, backgroundColor: C.bg, paddingHorizontal: 16, paddingTop: 6 + topoExtra, paddingBottom: 8,
            borderBottomWidth: 1, borderColor: C.line, shadowColor: '#000', shadowOpacity: 0.12, shadowRadius: 6, shadowOffset: { width: 0, height: 3 }, elevation: 5 }}>
            {presa.no}
          </View>
        )}
        {mostrarTopo && !(rodape && tec.alt > 0) && (
          <Pressable onPress={() => rolagemRef.current?.scrollTo({ y: 0, animated: true })} accessibilityRole="button" accessibilityLabel={t('Voltar ao topo')}
            style={{ position: 'absolute', right: 12, bottom: 16, zIndex: 29, paddingHorizontal: 12, height: 36, borderRadius: 18, alignItems: 'center', justifyContent: 'center',
              backgroundColor: alpha(C.card, 0.96), borderWidth: 1, borderColor: C.line, shadowColor: '#000', shadowOpacity: 0.15, shadowRadius: 5, shadowOffset: { width: 0, height: 2 }, elevation: 4 }}>
            <Text style={{ color: C.text, fontWeight: '800', fontSize: 13 }}>⬆ {t('Topo')}</Text>
          </Pressable>
        )}
        {!!rodape && tec.alt > 0 && (
          <View onLayout={e => { altRodape.current = e.nativeEvent.layout.height; }}
            style={{ position: 'absolute', left: 0, right: 0, bottom: baixo, zIndex: 31, backgroundColor: C.bg, paddingHorizontal: 8, paddingVertical: 6,
              borderTopWidth: 1, borderColor: C.line, shadowColor: '#000', shadowOpacity: 0.12, shadowRadius: 6, shadowOffset: { width: 0, height: -2 }, elevation: 6 }}>
            {rodape.no}
          </View>
        )}
      </View>
    </BarraFixaCtx.Provider>
  );
}
